import React, { useMemo, useState } from "react";
import jsPDF from "jspdf";
import pptxgen from "pptxgenjs";
import samsungLogo from "./assets/Samsung_wordmark.png";

import sampleMohmUW from "./assets/sample_mohm_uw.png";
import sampleMohmUV from "./assets/sample_mohm_uv.png";
import sampleMohmWV from "./assets/sample_mohm_wv.png";
import sampleWeightControl from "./assets/sample_weight_control.png";
import sampleProductLabel from "./assets/sample_product_label.jpg";
import sampleCompressorLabel from "./assets/sample_compressor_label.jpg";
import sampleArvSeparator from "./assets/sample_arv_separator.jpg";
import sampleSeparatorDetail from "./assets/sample_separator_detail.jpg";
import sampleOilColors from "./assets/sample_oil_colors.jpg";
import dxBataryaImg from "./assets/dx_batarya_yerlesim.png";

const compressorData = {
  DS2GR7046F: 0.2,
  DS2GT7046E: 0.12,
  "DS-GB052FA": 0.206,
  "DS-GB052FB": 0.125,
  "DS-GB066FA": 0.14,
  "DS4GJ5066E / DS4GT5066E": 0.073,
  "DS-GB070FA": 0.115,
  DS4GJ5080F: 0.101,
  DS4GR7066F: 0.14,
  DS4GM7090F: 0.121,
  DS4HC5066F: 0.269,
  DS4ZB5080F: 0.181,
  DS4HF8066F: 0.168,
};

const STANDARD_HP_OPTIONS = [8, 10, 12, 14, 16, 18, 20, 22, 24, 26];
const ECO_HP_OPTIONS = [4, 5, 6, 8, 10, 12];

const STANDARD_MODEL_MAP = {
  8: ["AM080AXVAGH/EU"],
  10: ["AM100AXVAGH/EU"],
  12: ["AM120AXVAGH/EU"],
  14: ["AM140AXVAGH/EU"],
  16: ["AM160AXVAGH/EU"],
  18: ["AM180AXVAGH/EU"],
  20: ["AM200AXVAGH/EU"],
  22: ["AM220AXVAGH/EU"],
  24: ["AM240AXVAGH/EU"],
  26: ["AM260AXVAGH/EU"],
};

const ECO_MODEL_MAP = {
  4: ["AM040BXMDEH/EU", "AM040BXMDGR/EU"],
  5: ["AM050BXMDEH/EU", "AM050BXMDGR/EU"],
  6: ["AM060BXMDGR/EU"],
  8: ["AM080BXMWGH/EU"],
  10: ["AM100BXMWGH/EU"],
  12: ["AM120BXMWGH/EU"],
};

const FIELD_CIRCUIT_COUNT = 5;

const createEmptyFieldCircuits = () =>
  Array.from({ length: FIELD_CIRCUIT_COUNT }, () => ({ units: [], eev: "" }));

const createEmptyFieldSharedGroups = () => [
  {
    unit: null,
    circuitNos: [],
    eev: "",
  },
];

const FIELD_SHARED_CONNECTION_WARNING =
  "Tek gövde dış ünitenin birden fazla bağımsız DX devreyi ortak beslemesi uygun değildir. Her devre, kendi kapasite ve EEV kontrolüne göre ayrı değerlendirilmelidir.";

const getMaxFieldUnitsPerCircuit = (mode) => (mode === "Eco" ? 1 : 4);

const buildOutdoorUnitOptions = (mode) => {
  const hpOptions = mode === "Eco" ? ECO_HP_OPTIONS : STANDARD_HP_OPTIONS;
  const modelMap = mode === "Eco" ? ECO_MODEL_MAP : STANDARD_MODEL_MAP;

  return hpOptions.flatMap((hp) =>
    (modelMap[hp] || []).map((model) => ({
      hp,
      model,
      value: `${hp}|${model}`,
      label: `${model} - ${hp} HP`,
    }))
  );
};

const parseOutdoorUnitValue = (value) => {
  const [hpText, ...modelParts] = String(value || "").split("|");
  const hp = parseFloat(hpText);
  const model = modelParts.join("|");

  if (!Number.isFinite(hp) || !model) return null;
  return { hp, model };
};

const AIRFLOW_TOLERANCE = 0.05;

const calcResistance = (base, temp) => {
  if (!base || Number.isNaN(temp)) return 0;
  return base * (1 + 0.00393 * (temp - 25));
};

const formatMilliOhm = (value, digits = 3) => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(digits).replace(/^0(?=\.)/, "");
};

const parseMilliOhmInput = (value) => {
  const raw = String(value || "").trim().replace(",", ".");

  if (!raw) return NaN;

  if (raw.includes(".")) {
    return parseFloat(raw);
  }

  if (/^\d+$/.test(raw)) {
    return parseFloat(`0.${raw}`);
  }

  return NaN;
};

const formatMilliOhmInput = (value, digits = 3) => {
  const num = parseMilliOhmInput(value);
  if (!Number.isFinite(num)) return "";
  return formatMilliOhm(num, digits);
};

const formatNumber = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const getSelectionBasis = ({ hasCapacity, hasAirflow, capacityHp, airflowHp }) => {
  const diff = Math.abs(capacityHp - airflowHp);

  if (hasCapacity && hasAirflow) {
    if (diff <= 0.01) {
      return {
        label: "kW ve debi ihtiyacına göre",
        reason: `kW karşılığı ${formatNumber(capacityHp)} HP, debi karşılığı ${formatNumber(airflowHp)} HP. Değerler eşit kabul edildi.`,
      };
    }

    if (capacityHp > airflowHp) {
      return {
        label: "kW ihtiyacına göre",
        reason: `kW karşılığı ${formatNumber(capacityHp)} HP, debi karşılığı ${formatNumber(airflowHp)} HP. Büyük olan kW ihtiyacı seçimi belirledi.`,
      };
    }

    return {
      label: "debi ihtiyacına göre",
      reason: `kW karşılığı ${formatNumber(capacityHp)} HP, debi karşılığı ${formatNumber(airflowHp)} HP. Büyük olan debi ihtiyacı seçimi belirledi.`,
    };
  }

  if (hasCapacity) {
    return {
      label: "kW ihtiyacına göre",
      reason: `Sadece kW girildi. Seçim ${formatNumber(capacityHp)} HP kapasite ihtiyacına göre yapıldı.`,
    };
  }

  if (hasAirflow) {
    return {
      label: "debi ihtiyacına göre",
      reason: `Sadece debi girildi. Seçim ${formatNumber(airflowHp)} HP debi ihtiyacına göre yapıldı.`,
    };
  }

  return {
    label: "-",
    reason: "Kapasite veya debi bilgisi girilmedi.",
  };
};

const getModelNamesByModeAndHp = (mode, hp) => {
  const map = mode === "Eco" ? ECO_MODEL_MAP : STANDARD_MODEL_MAP;
  return map[hp] || [];
};

const buildStandardCombinations = () => {
  const combos = [];

  for (let i = 0; i < STANDARD_HP_OPTIONS.length; i += 1) {
    const a = STANDARD_HP_OPTIONS[i];
    combos.push([a]);

    for (let j = i; j < STANDARD_HP_OPTIONS.length; j += 1) {
      const b = STANDARD_HP_OPTIONS[j];
      if (a + b <= 40) combos.push([a, b]);

      for (let k = j; k < STANDARD_HP_OPTIONS.length; k += 1) {
        const c = STANDARD_HP_OPTIONS[k];
        if (a + b + c <= 40) combos.push([a, b, c]);
      }
    }
  }

  return combos;
};

const STANDARD_COMBINATIONS = buildStandardCombinations();

const getBalanceScore = (combo) => {
  if (!combo || combo.length === 0) return 999;
  return Math.max(...combo) - Math.min(...combo);
};

const buildComboLabel = (combo) => combo.map((hp) => `${hp} HP`).join(" + ");

const buildComboModelLabel = (mode, combo) =>
  combo
    .map((hp) => {
      const models = getModelNamesByModeAndHp(mode, hp);
      return `${hp} HP → ${models.join(" / ") || "-"}`;
    })
    .join(" | ");

const getEcoSelection = (requiredHp) => {
  const pickedHp = ECO_HP_OPTIONS.find((hp) => hp >= requiredHp);
  if (!pickedHp) return null;

  const units = [pickedHp];
  return {
    units,
    totalHp: pickedHp,
    unitCount: 1,
    oversize: pickedHp - requiredHp,
    balanceScore: 0,
    label: buildComboLabel(units),
    modelLabel: buildComboModelLabel("Eco", units),
  };
};

const getStandardCandidates = (requiredHp) => {
  const suitable = STANDARD_COMBINATIONS
    .filter((combo) => {
      const totalHp = combo.reduce((sum, hp) => sum + hp, 0);
      return totalHp >= requiredHp && totalHp <= 40;
    })
    .map((combo) => {
      const totalHp = combo.reduce((sum, hp) => sum + hp, 0);
      const unitCount = combo.length;
      const oversize = totalHp - requiredHp;
      const balanceScore = getBalanceScore(combo);

      return {
        units: combo,
        totalHp,
        unitCount,
        oversize,
        balanceScore,
        label: buildComboLabel(combo),
        modelLabel: buildComboModelLabel("Standart", combo),
      };
    });

  if (suitable.length === 0) return [];

  const minUnitCount = Math.min(...suitable.map((item) => item.unitCount));

  return suitable
    .filter((item) => item.unitCount === minUnitCount)
    .sort((a, b) => {
      if (a.balanceScore !== b.balanceScore) return a.balanceScore - b.balanceScore;
            if (a.oversize !== b.oversize) return a.oversize - b.oversize;
      return a.totalHp - b.totalHp;
    });
};

const getPerCircuitSelection = (mode, requiredHp) => {
  if (mode === "Eco") {
    const selection = getEcoSelection(requiredHp);
    return {
      selected: selection,
      alternatives: selection ? [selection] : [],
    };
  }

  const candidates = getStandardCandidates(requiredHp);
  return {
    selected: candidates[0] || null,
    alternatives: candidates.slice(0, 8),
  };
};

const evaluateCircuitPlan = (mode, totalRequiredHp, circuitCount) => {
  if (circuitCount < 1 || circuitCount > 5) return null;

  const perCircuitRequiredHp = totalRequiredHp / circuitCount;

  if (mode === "Eco") {
    if (perCircuitRequiredHp > 12) return null;
  } else {
    if (perCircuitRequiredHp > 40) return null;
  }

  const selection = getPerCircuitSelection(mode, perCircuitRequiredHp);
  if (!selection.selected) return null;

  const totalOutdoorUnitCount = selection.selected.unitCount * circuitCount;
  const totalOversize = selection.selected.oversize * circuitCount;

  return {
    circuitCount,
    perCircuitRequiredHp,
    selectedPerCircuit: selection.selected,
    alternativesPerCircuit: selection.alternatives,
    totalOutdoorUnitCount,
    totalOversize,
    totalSelectedHp: selection.selected.totalHp * circuitCount,
    totalSelectedKw: selection.selected.totalHp * 3.5 * circuitCount,
  };
};

function App() {
  const [activePage, setActivePage] = useState("compressor");
  const [step, setStep] = useState(1);

  const [serviceCompany, setServiceCompany] = useState("");
  const [servicePerson, setServicePerson] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [customerName, setCustomerName] = useState("");

  const [photos, setPhotos] = useState({
    modelSerialLabel: "",
    oldCompLabel: "",
    newCompLabel: "",
    mohmUW: "",
    mohmUV: "",
    mohmWV: "",
    oldCompWeight: "",
    newCompWeight: "",
    arvPhoto: "",
    separatorPhoto: "",
    oilColorValvePhoto: "",
    mainOilColorPhoto: "",
    otherCompressorsOilPhoto: "",
  });

  const [selectedModel, setSelectedModel] = useState("");
  const [temperature, setTemperature] = useState("");
  const [measuredUW, setMeasuredUW] = useState("");
  const [measuredUV, setMeasuredUV] = useState("");
  const [measuredWV, setMeasuredWV] = useState("");

  const [mechanicalSound, setMechanicalSound] = useState("");
  const [weightStatus, setWeightStatus] = useState("");
  const [valveSeparatorIssue, setValveSeparatorIssue] = useState("");
  const [oilCameFromValves, setOilCameFromValves] = useState("");
  const [oilColorAfterValve, setOilColorAfterValve] = useState("");
  const [mainOilColorSuitable, setMainOilColorSuitable] = useState("");
  const [otherCompressorsOilSuitable, setOtherCompressorsOilSuitable] = useState("");

  const [flowResult, setFlowResult] = useState("");
  const [flowSummary, setFlowSummary] = useState({
    finalTitle: "",
    finalText: "",
    actions: [],
    path: "",
    detailRows: [],
  });

  const [ahuProjectName, setAhuProjectName] = useState("");
  const [ahuServiceName, setAhuServiceName] = useState("");
  const [ahuDate, setAhuDate] = useState("");
  const [ahuOutdoorMode, setAhuOutdoorMode] = useState("Standart");
  const [ahuCoolingCapacity, setAhuCoolingCapacity] = useState("");
  const [ahuAirflow, setAhuAirflow] = useState("");
  const [ahuEvapTemp, setAhuEvapTemp] = useState("");
  const [ahuSuperheat, setAhuSuperheat] = useState("");
  const [ahuSubcool, setAhuSubcool] = useState("");
  const [ahuCondTemp, setAhuCondTemp] = useState("");
  const [ahuEnteringAirTemp, setAhuEnteringAirTemp] = useState("");
  const [ahuPressureDrop, setAhuPressureDrop] = useState("");
  const [ahuAirVelocity, setAhuAirVelocity] = useState("");
  const [ahuCircuitCount, setAhuCircuitCount] = useState("Otomatik");

  const [ahuFieldCompareEnabled, setAhuFieldCompareEnabled] = useState(false);
  const [ahuFieldOutdoorMode, setAhuFieldOutdoorMode] = useState("Standart");
  const [ahuFieldConnectionType, setAhuFieldConnectionType] = useState("separate");
  const [ahuFieldCircuits, setAhuFieldCircuits] = useState(createEmptyFieldCircuits);
  const [ahuFieldSharedGroups, setAhuFieldSharedGroups] = useState(createEmptyFieldSharedGroups);
  const [ahuFieldNote, setAhuFieldNote] = useState("");

  const pageTitle = {
    1: "Genel Bilgiler",
    2: "Cihaz Model Etiketi",
    3: "Kompresör Model Etiketi",
    4: "Kompresör mΩ Değerleri",
    5: "Eski ve Yeni Kompresör Terazi Kontrolü",
    6: "Mekanik Ses Kontrolü",
    7: "Kompresör Ağırlık Kontrolü",
    8: "ARV ve Yağ Separatör Kontrolü",
    9: "ARV / Yağ Separatör Yağ Çıkış Kontrolü",
    10: "ARV / Yağ Separatör Yağ Renk KontrolÜ",
    11: "Sökülen Kompresör Yağ Renk Kontrolü",
    12: "Diğer Kompresörler ve Akümülatör Yağ Renk KontrolÜ",
    13: "Sonuç",
  };

  const baseResistance = selectedModel ? compressorData[selectedModel] : 0;

  const expectedResistance = useMemo(() => {
    const tempNumber = parseFloat(temperature);
    return calcResistance(baseResistance, tempNumber);
  }, [baseResistance, temperature]);

  const minResistance = expectedResistance * 0.9;
  const maxResistance = expectedResistance * 1.1;

  const isInRange = (value) => {
    const num = parseMilliOhmInput(value);
    if (Number.isNaN(num) || expectedResistance <= 0) return false;
    return num >= minResistance && num <= maxResistance;
  };

  const uwOk = isInRange(measuredUW);
  const uvOk = isInRange(measuredUV);
  const wvOk = isInRange(measuredWV);

  const hasMeasuredValue = (value) => String(value || "").trim() !== "";
  const uwStatus = hasMeasuredValue(measuredUW) ? uwOk : null;
  const uvStatus = hasMeasuredValue(measuredUV) ? uvOk : null;
  const wvStatus = hasMeasuredValue(measuredWV) ? wvOk : null;

  const mohmOverall =
    expectedResistance > 0 &&
    measuredUW !== "" &&
    measuredUV !== "" &&
    measuredWV !== "" &&
    uwOk &&
    uvOk &&
    wvOk;

  const ahuCalc = useMemo(() => {
    const capacityKw = parseFloat(ahuCoolingCapacity);
    const airflowM3h = parseFloat(ahuAirflow);
    const evap = parseFloat(ahuEvapTemp);
    const superheat = parseFloat(ahuSuperheat);
    const subcool = parseFloat(ahuSubcool);
    const cond = parseFloat(ahuCondTemp);
    const enteringAirTemp = parseFloat(ahuEnteringAirTemp);
    const pressureDrop = parseFloat(ahuPressureDrop);
    const airVelocity = parseFloat(ahuAirVelocity);

    const coeff = 480;

    const hasCapacity = Number.isFinite(capacityKw) && capacityKw > 0;
    const hasAirflow = Number.isFinite(airflowM3h) && airflowM3h > 0;

    const capacityHp = hasCapacity ? capacityKw / 3.5 : 0;
    const airflowHp = hasAirflow ? airflowM3h / coeff : 0;

    const requiredTotalHp =
      hasCapacity && hasAirflow
        ? Math.max(capacityHp, airflowHp)
        : hasCapacity
        ? capacityHp
        : hasAirflow
        ? airflowHp
        : 0;
            const resolvedCapacityKw = hasCapacity ? capacityKw : requiredTotalHp * 3.5;
    const resolvedAirflowM3h = hasAirflow ? airflowM3h : requiredTotalHp * coeff;

    const selectionBasis = getSelectionBasis({
      hasCapacity,
      hasAirflow,
      capacityHp,
      airflowHp,
    });

    const hasLoadInput = requiredTotalHp > 0 && (hasCapacity || hasAirflow);
    const isRequiredFilled = (value) => String(value || "").trim() !== "";

    const buildPlanMetrics = (plan) => {
      if (!plan?.selectedPerCircuit) return null;

      const circuitCount = plan.circuitCount || 1;
      const totalRequiredAirflow = requiredTotalHp * coeff;
      const minAirflow = plan.totalSelectedHp * 432;
      const maxAirflow = plan.totalSelectedHp * 576;

      const airflowOk = hasAirflow ? airflowM3h >= minAirflow && airflowM3h <= maxAirflow : true;

      let airflowReason = `Debi bilgisi girilmedi. Teorik debi bilgi amaçlıdır; debi kontrol aralığı seçilen ${formatNumber(
        plan.totalSelectedHp
      )} HP dış üniteye göre Min ${formatNumber(minAirflow)} m³/h / Max ${formatNumber(maxAirflow)} m³/h olarak değerlendirilir.`;
      if (hasAirflow) {
        if (airflowOk) {
          airflowReason = `Debi uygun. Min: ${formatNumber(minAirflow)} m³/h, Max: ${formatNumber(maxAirflow)} m³/h`;
          const range = maxAirflow - minAirflow;
          if (range > 0) {
            const distToMin = (airflowM3h - minAirflow) / range;
            const distToMax = (maxAirflow - airflowM3h) / range;
            if (distToMin < 0.05 || distToMax < 0.05) {
              airflowReason += " (Uyarı: Sınırlara yakın)";
            }
          }
        } else {
          airflowReason = `Debi uygun değil. Min: ${formatNumber(minAirflow)} m³/h, Max: ${formatNumber(maxAirflow)} m³/h, girilen: ${formatNumber(airflowM3h)} m³/h`;
        }
      }

      const eevPerCircuit = Math.ceil(plan.perCircuitRequiredHp / 10) || 0;
      const totalEev = eevPerCircuit * circuitCount;

      const circuitModelGroups = Array.from({ length: circuitCount }, (_, circuitIndex) => ({
        circuitNo: circuitIndex + 1,
        units: plan.selectedPerCircuit.units.map((hp, unitIndex) => ({
          unitNo: unitIndex + 1,
          hp,
          models: getModelNamesByModeAndHp(ahuOutdoorMode, hp),
        })),
        totalHp: plan.selectedPerCircuit.totalHp,
        totalKw: plan.selectedPerCircuit.totalHp * 3.5,
      }));

      return {
        ...plan,
        totalRequiredAirflow,
        minAirflow,
        maxAirflow,
        airflowOk,
        airflowReason,
        totalEev,
        circuitModelGroups,
        roundedUpHp: plan.totalSelectedHp - requiredTotalHp,
        roundedUpKw: plan.totalSelectedKw - requiredTotalHp * 3.5,
      };
    };

    const rawPlans = !hasLoadInput
      ? []
      : ahuOutdoorMode === "Eco"
      ? [1, 2, 3, 4, 5]
          .map((count) => evaluateCircuitPlan("Eco", requiredTotalHp, count))
          .filter(Boolean)
      : [1, 2, 3, 4, 5]
          .map((count) => evaluateCircuitPlan("Standart", requiredTotalHp, count))
          .filter(Boolean);

    const candidatePlans = rawPlans
      .map((plan) => buildPlanMetrics(plan))
      .filter(Boolean)
      .sort((a, b) => {
        const aAirOk = a.airflowOk ? 1 : 0;
        const bAirOk = b.airflowOk ? 1 : 0;
        if (aAirOk !== bAirOk) return bAirOk - aAirOk;

        if (a.totalOutdoorUnitCount !== b.totalOutdoorUnitCount) {
          return a.totalOutdoorUnitCount - b.totalOutdoorUnitCount;
        }
        if (a.totalOversize !== b.totalOversize) {
          return a.totalOversize - b.totalOversize;
        }
        if (a.selectedPerCircuit.balanceScore !== b.selectedPerCircuit.balanceScore) {
          return a.selectedPerCircuit.balanceScore - b.selectedPerCircuit.balanceScore;
        }
        return a.circuitCount - b.circuitCount;
      });

    const autoPlan = candidatePlans.find((item) => item.airflowOk) || candidatePlans[0] || null;

    let activePlan = null;
    if (ahuCircuitCount === "Otomatik") {
      activePlan = autoPlan;
    } else {
      const manualCount = parseInt(ahuCircuitCount, 10) || 1;
      activePlan = candidatePlans.find((item) => item.circuitCount === manualCount) || null;
    }

    const circuitCount =
      activePlan?.circuitCount ||
      (hasLoadInput ? (ahuCircuitCount === "Otomatik" ? 1 : Number(ahuCircuitCount) || 1) : 0);

    const perCircuitRequiredHp =
      activePlan?.perCircuitRequiredHp || (hasLoadInput && circuitCount ? requiredTotalHp / circuitCount : 0);

    const theoreticalAirflowM3h = requiredTotalHp * coeff;
    const selectedStandardAirflowM3h = activePlan ? activePlan.totalSelectedHp * coeff : 0;
    const enteredAirflowText = hasAirflow ? `${formatNumber(airflowM3h)} m³/h` : "Girilmedi";
    const selectedAirflowRangeText = activePlan
      ? `Min ${formatNumber(activePlan.minAirflow)} m³/h / Standart ${formatNumber(
          selectedStandardAirflowM3h
        )} m³/h / Max ${formatNumber(activePlan.maxAirflow)} m³/h`
      : "Seçim bekleniyor";

    const circuitFeasible =
      hasLoadInput &&
      (ahuOutdoorMode === "Eco"
        ? perCircuitRequiredHp <= 12
        : perCircuitRequiredHp <= 40);

    let systemReason = "";
    if (!hasCapacity && !hasAirflow) {
      systemReason = "Kapasite veya debi bilgisi girilmelidir.";
    } else if (!circuitFeasible) {
      systemReason =
        ahuOutdoorMode === "Eco"
          ? `Eco modda bu yük için seçim mümkün değil. Devre başına HP ${formatNumber(
              perCircuitRequiredHp
            )} ve Eco üst sınırı 12 HP.`
          : `${circuitCount} devre ile seçim mümkün değil. Devre başına gereken HP ${formatNumber(
              perCircuitRequiredHp
            )} ve üst sınır 40 HP.`;
    } else if (!activePlan?.selectedPerCircuit) {
      systemReason = "Bu devre sayısı için uygun dış ünite kombinasyonu bulunamadı.";
    } else if (!activePlan.airflowOk) {
      systemReason = activePlan.airflowReason;
    } else {
      systemReason = "Tüm temel seçim kuralları sağlandı.";
    }

    const validationRows = [
      {
        label: "Proje Adı",
        value: isRequiredFilled(ahuProjectName) ? ahuProjectName : "-",
        ok: isRequiredFilled(ahuProjectName),
        rule: "Zorunlu alan",
      },
      {
        label: "Servis Adı",
        value: isRequiredFilled(ahuServiceName) ? ahuServiceName : "-",
        ok: isRequiredFilled(ahuServiceName),
        rule: "Zorunlu alan",
      },
      {
        label: "Tarih",
        value: isRequiredFilled(ahuDate) ? ahuDate : "-",
        ok: isRequiredFilled(ahuDate),
        rule: "Zorunlu alan",
      },
      {
        label: "Toplam Soğutma Kapasitesi",
        value: Number.isFinite(capacityKw) ? `${formatNumber(capacityKw)} kW` : "-",
        ok: hasCapacity,
        rule: "> 0 kW zorunlu",
      },
      {
        label: "Santral Debisi",
        value: Number.isFinite(airflowM3h) ? `${formatNumber(airflowM3h)} m³/h` : "-",
        ok: hasAirflow,
        rule: "> 0 m³/h zorunlu",
      },
      {
        label: "Evaporasyon Sıcaklığı",
        value: Number.isFinite(evap) ? `${formatNumber(evap)} °C` : "-",
        ok: Number.isFinite(evap) && evap >= 5 && evap <= 15,
        rule: "5 – 15 °C",
      },
      {
        label: "Superheat",
        value: Number.isFinite(superheat) ? `${formatNumber(superheat)} °C` : "-",
        ok: Number.isFinite(superheat) && superheat >= 1,
        rule: "≥ 1 °C",
      },
      {
        label: "Subcool",
        value: Number.isFinite(subcool) ? `${formatNumber(subcool)} °C` : "-",
        ok: Number.isFinite(subcool) && subcool >= 2 && subcool <= 5,
        rule: "2 – 5 °C",
      },
      {
        label: "Kondenzasyon Sıcaklığı",
        value: Number.isFinite(cond) ? `${formatNumber(cond)} °C` : "-",
        ok: Number.isFinite(cond) && cond >= 35 && cond <= 55,
        rule: "35 – 55 °C",
      },
      {
        label: "Batarya Giriş Havası Sıcaklığı",
        value: Number.isFinite(enteringAirTemp) ? `${formatNumber(enteringAirTemp)} °C` : "-",
        ok: Number.isFinite(enteringAirTemp) && enteringAirTemp >= 10 && enteringAirTemp <= 32,
        rule: "10 – 32 °C",
      },
      {
        label: "R410A Basınç Kaybı",
        value: Number.isFinite(pressureDrop) ? `${formatNumber(pressureDrop)} kPa` : "-",
        ok: Number.isFinite(pressureDrop) && pressureDrop >= 7 && pressureDrop <= 50,
        rule: "7 – 50 kPa",
      },
      {
        label: "Hava Hızı",
        value: Number.isFinite(airVelocity) ? `${formatNumber(airVelocity)} m/s` : "-",
        ok: Number.isFinite(airVelocity) && airVelocity < 2.5,
        rule: "< 2.5 m/s",
      },
      {
        label: "Toplam HP İhtiyacı",
        value: requiredTotalHp > 0 ? `${formatNumber(requiredTotalHp)} HP` : "-",
        ok: requiredTotalHp > 0,
        rule: "Girilen kW ve debi değerinden hesaplanan ham HP ihtiyacı",
      },
      {
        label: "Teorik Debi Karşılığı",
        value: requiredTotalHp > 0 ? `${formatNumber(theoreticalAirflowM3h)} m³/h` : "-",
        ok: requiredTotalHp > 0,
        rule: "Ham HP ihtiyacı × 480. Bu değer seçilen dış ünitenin debi aralığı değildir.",
      },
      {
        label: "Dış Ünite Seçim Kriteri",
        value: selectionBasis.label,
        ok: requiredTotalHp > 0,
        rule: selectionBasis.reason,
      },
      {
        label: "Ürün Bazlı Yuvarlama",
        value: activePlan ? `+${formatNumber(activePlan.roundedUpHp)} HP` : "-",
        ok: Boolean(activePlan),
        rule: "Ham HP ihtiyacı mevcut dış ünite ürün kademesine yukarı tamamlanır",
      },
      {
        label: "Seçilen Dış Ünite Debi Aralığı",
        value: activePlan
          ? `${formatNumber(activePlan.minAirflow)} – ${formatNumber(activePlan.maxAirflow)} m³/h`
          : "-",
        ok: Boolean(activePlan),
        rule: activePlan
          ? `${formatNumber(activePlan.totalSelectedHp)} HP seçime göre Min / Max aralığı. Standart debi: ${formatNumber(
              selectedStandardAirflowM3h
            )} m³/h`
          : "Seçim bekleniyor",
      },
      {
        label: "Girilen Santral Debisi Kontrolü",
        value: hasAirflow ? `${formatNumber(airflowM3h)} m³/h` : "Debi girilmedi",
        ok: Boolean(activePlan) && hasAirflow && activePlan.airflowOk,
        rule: activePlan
          ? hasAirflow
            ? selectedAirflowRangeText
            : `Debi girişi yapılmadı. Kontrol aralığı: ${selectedAirflowRangeText}`
          : "Seçim bekleniyor",
      },
      {
        label: "Seçim Modu",
        value: ahuOutdoorMode,
        ok: true,
        rule: ahuOutdoorMode === "Eco" ? "Her devrede tek Eco dış ünite" : "Standart kombinasyon",
      },
      {
        label: "Toplam EEV",
        value: activePlan ? `${activePlan.totalEev} adet` : "-",
        ok: Boolean(activePlan),
        rule: "Devre planına göre hesaplanır",
      },
    ];

    const hasValidPlan = Boolean(activePlan?.selectedPerCircuit) && circuitFeasible;
    const failedValidationRows = validationRows.filter((row) => row.ok === false);

    const allOk =
      hasValidPlan &&
      validationRows.every((row) => row.ok === true);

    const selectionIssue =
      systemReason && systemReason !== "Tüm temel seçim kuralları sağlandı."
        ? systemReason
        : "";

    const missingValidationRows = failedValidationRows.filter((row) => row.value === "-" || String(row.value).includes("girilmedi"));
    const invalidValidationRows = failedValidationRows.filter((row) => !missingValidationRows.includes(row));

    const missingIssue =
      missingValidationRows.length > 0
        ? `Eksik zorunlu alanlar: ${missingValidationRows.map((row) => row.label).join(", ")}.`
        : "";

    const invalidIssue =
      invalidValidationRows.length > 0
        ? `Kontrol dışı değerler: ${invalidValidationRows
            .map((row) => `${row.label} ${row.value} (Kural: ${row.rule})`)
            .join(", ")}.`
        : "";

    const validationIssue = [missingIssue, invalidIssue].filter(Boolean).join(" ");

    const finalSystemReason = allOk
      ? "Tüm temel seçim kuralları sağlandı."
      : [selectionIssue, validationIssue].filter(Boolean).join(" ") ||
        "Bazı kontrol kriterleri uygun değil.";

    const suitableCandidatePlans = candidatePlans.filter((item) => item.airflowOk);

    const alternatives = suitableCandidatePlans.slice(0, 5).map((item, index) => ({
      id: `${item.circuitCount}-${item.selectedPerCircuit?.label || index}`,
      rank: index + 1,
      isRecommended:
        autoPlan &&
        item.circuitCount === autoPlan.circuitCount &&
        item.selectedPerCircuit?.label === autoPlan.selectedPerCircuit?.label,
      circuitCount: item.circuitCount,
      totalHp: item.totalSelectedHp,
      totalKw: item.totalSelectedKw,
      eevTotal: item.totalEev,
      oversize: item.totalOversize,
      roundedUpHp: item.roundedUpHp,
      roundedUpKw: item.roundedUpKw,
      airflowOk: item.airflowOk,
      airflowReason: item.airflowReason,
      minAirflow: item.minAirflow,
      maxAirflow: item.maxAirflow,
      enteredAirflow: hasAirflow ? airflowM3h : null,
      selectionBasis,
      modelGroups: item.circuitModelGroups || [],
      badge: item.airflowOk
        ? item === autoPlan
          ? "Önerilen"
          : "Uygun"
        : "Debi Uygun Değil",
    }));

    return {
      hasCapacity,
      hasAirflow,
      hasLoadInput,
      resolvedCapacityKw,
      resolvedAirflowM3h,
      theoreticalAirflowM3h,
      selectedStandardAirflowM3h,
      enteredAirflowText,
      selectedAirflowRangeText,
      coeff,
      capacityHp,
      airflowHp,
      selectionBasis,
      requiredTotalHp,
      autoPlan,
      activePlan,
      circuitCount,
      perCircuitRequiredHp,
      totalSelectedHp: activePlan?.totalSelectedHp || 0,
      totalSelectedKw: activePlan?.totalSelectedKw || 0,
      minAirflow: activePlan?.minAirflow || 0,
      maxAirflow: activePlan?.maxAirflow || 0,
      totalEev: activePlan?.totalEev || 0,
      circuitModelGroups: activePlan?.circuitModelGroups || [],
      validationRows,
      allOk,
      alternatives,
      circuitFeasible,
      failedValidationRows,
      systemReason: finalSystemReason,
      airflowReason:
        activePlan?.airflowReason ||
        `Gerekli debi: ${formatNumber(requiredTotalHp * coeff)} m³/h`,
      totalRequiredAirflow: theoreticalAirflowM3h,
      roundedUpHp: activePlan?.roundedUpHp || 0,
      roundedUpKw: activePlan?.roundedUpKw || 0,
    };
  }, [
    ahuAirflow,
    ahuCircuitCount,
    ahuCondTemp,
    ahuCoolingCapacity,
    ahuEvapTemp,
    ahuOutdoorMode,
    ahuSubcool,
    ahuSuperheat,
    ahuEnteringAirTemp,
    ahuPressureDrop,
    ahuAirVelocity,
    ahuProjectName,
    ahuServiceName,
    ahuDate,
  ]);


  const ahuFieldOutdoorOptions = useMemo(
    () => buildOutdoorUnitOptions(ahuFieldOutdoorMode),
    [ahuFieldOutdoorMode]
  );

  const getAhuFieldMaxUnits = () => getMaxFieldUnitsPerCircuit(ahuFieldOutdoorMode);

  const getDefaultAhuFieldUnit = (mode = ahuFieldOutdoorMode) => {
    const option = buildOutdoorUnitOptions(mode)[0];
    return option ? { hp: option.hp, model: option.model } : null;
  };

  const handleAhuFieldModeChange = (mode) => {
    setAhuFieldOutdoorMode(mode);
    const maxUnits = getMaxFieldUnitsPerCircuit(mode);
    const defaultUnit = getDefaultAhuFieldUnit(mode);

    setAhuFieldCircuits((prev) =>
      prev.map((circuit) => ({
        ...circuit,
        units: circuit.units.slice(0, maxUnits).map(() => defaultUnit).filter(Boolean),
      }))
    );

    setAhuFieldSharedGroups((prev) =>
      prev.map((group) => ({
        ...group,
        unit: group.unit ? defaultUnit : group.unit,
      }))
    );
  };

  const handleAhuFieldConnectionTypeChange = (type) => {
    setAhuFieldConnectionType(type);
    if (type === "shared") {
      const defaultUnit = getDefaultAhuFieldUnit();
      setAhuFieldSharedGroups((prev) =>
        prev.map((group) => ({
          ...group,
          unit: group.unit || defaultUnit,
        }))
      );
    }
  };

  const addAhuFieldUnit = (circuitIndex) => {
    setAhuFieldCircuits((prev) => {
      const defaultUnit = getDefaultAhuFieldUnit();
      if (!defaultUnit) return prev;
      const maxUnits = getMaxFieldUnitsPerCircuit(ahuFieldOutdoorMode);
      return prev.map((circuit, index) => {
        if (index !== circuitIndex) return circuit;
        if (circuit.units.length >= maxUnits) return circuit;
        return { ...circuit, units: [...circuit.units, defaultUnit] };
      });
    });
  };

  const updateAhuFieldUnit = (circuitIndex, unitIndex, value) => {
    const parsed = parseOutdoorUnitValue(value);
    if (!parsed) return;
    setAhuFieldCircuits((prev) =>
      prev.map((circuit, index) => {
        if (index !== circuitIndex) return circuit;
        return {
          ...circuit,
          units: circuit.units.map((unit, currentUnitIndex) =>
            currentUnitIndex === unitIndex ? parsed : unit
          ),
        };
      })
    );
  };

  const removeAhuFieldUnit = (circuitIndex, unitIndex) => {
    setAhuFieldCircuits((prev) =>
      prev.map((circuit, index) => {
        if (index !== circuitIndex) return circuit;
        return { ...circuit, units: circuit.units.filter((_, currentUnitIndex) => currentUnitIndex !== unitIndex) };
      })
    );
  };

  const updateAhuFieldCircuitEev = (circuitIndex, value) => {
    setAhuFieldCircuits((prev) =>
      prev.map((circuit, index) => (index === circuitIndex ? { ...circuit, eev: value } : circuit))
    );
  };

  const addAhuFieldSharedGroup = () => {
    const defaultUnit = getDefaultAhuFieldUnit();
    setAhuFieldSharedGroups((prev) => [
      ...prev,
      { unit: defaultUnit, circuitNos: [], eev: "" },
    ]);
  };

  const updateAhuFieldSharedUnit = (groupIndex, value) => {
    const parsed = parseOutdoorUnitValue(value);
    if (!parsed) return;
    setAhuFieldSharedGroups((prev) =>
      prev.map((group, index) => (index === groupIndex ? { ...group, unit: parsed } : group))
    );
  };

  const toggleAhuFieldSharedCircuit = (groupIndex, circuitNo) => {
    setAhuFieldSharedGroups((prev) =>
      prev.map((group, index) => {
        if (index !== groupIndex) return group;
        const exists = group.circuitNos.includes(circuitNo);
        const nextCircuitNos = exists
          ? group.circuitNos.filter((item) => item !== circuitNo)
          : [...group.circuitNos, circuitNo].sort((a, b) => a - b);
        return { ...group, circuitNos: nextCircuitNos };
      })
    );
  };

  const updateAhuFieldSharedEev = (groupIndex, value) => {
    setAhuFieldSharedGroups((prev) =>
      prev.map((group, index) => (index === groupIndex ? { ...group, eev: value } : group))
    );
  };

  const removeAhuFieldSharedGroup = (groupIndex) => {
    setAhuFieldSharedGroups((prev) => {
      const next = prev.filter((_, index) => index !== groupIndex);
      return next.length > 0 ? next : createEmptyFieldSharedGroups();
    });
  };

  const clearAhuFieldComparison = () => {
    setAhuFieldCircuits(createEmptyFieldCircuits());
    setAhuFieldSharedGroups(createEmptyFieldSharedGroups());
    setAhuFieldConnectionType("separate");
    setAhuFieldNote("");
  };

  const ahuFieldComparison = useMemo(() => {
    // Saha karşılaştırmasında "olması gereken" hesap, kullanıcı manuelde
    // uygunsuz devre sayısı seçmiş olsa bile otomatik uygun plana göre alınır.
    // Böylece 40 HP/devre sınırı ve devre dengeleme kuralı boş kalmaz.
    const expectedPlan = ahuCalc?.autoPlan || ahuCalc?.activePlan || null;
    const hasExpectedSelection = Boolean(expectedPlan);
    const expectedHp = expectedPlan?.totalSelectedHp || 0;
    const expectedKw = expectedPlan?.totalSelectedKw || 0;
    const expectedCircuitCount = expectedPlan?.circuitCount || 0;
    const expectedEev = expectedPlan?.totalEev || 0;
    const expectedMinAirflow = expectedPlan?.minAirflow || 0;
    const expectedStdAirflow = expectedPlan?.totalSelectedHp ? expectedPlan.totalSelectedHp * 480 : 0;
    const expectedMaxAirflow = expectedPlan?.maxAirflow || 0;
    const isSharedConnection = ahuFieldConnectionType === "shared";

    const expectedCircuitGroups = expectedPlan?.circuitModelGroups || [];
    const expectedCircuitText = expectedCircuitGroups.length > 0
      ? expectedCircuitGroups.map((circuit) => `Devre ${circuit.circuitNo}: ${formatNumber(circuit.totalHp)} HP / ${formatNumber(circuit.totalKw)} kW`).join(" | ")
      : "Seçim bekleniyor";

    const expectedCircuitHpByNo = expectedCircuitGroups.reduce((acc, circuit) => {
      acc[circuit.circuitNo] = circuit.totalHp;
      return acc;
    }, {});

    let appliedCircuits = [];
    let appliedSharedGroups = [];
    let appliedTotalHp = 0;
    let appliedTotalKw = 0;
    let appliedCircuitCount = 0;
    let appliedTotalEev = 0;
    let appliedCircuitText = "-";
    let appliedModelText = "-";
    let sameCircuitDistribution = false;
    let missingEevCircuits = [];
    let wrongEevCircuits = [];
    let limitIssues = [];
    let hasSharedMultiCircuit = false;

    if (isSharedConnection) {
      appliedSharedGroups = ahuFieldSharedGroups
        .map((group, index) => {
          const unit = group.unit;
          const circuitNos = [...new Set(group.circuitNos || [])].sort((a, b) => a - b);
          const totalHp = unit ? Number(unit.hp) || 0 : 0;
          const totalKw = totalHp * 3.5;
          const eevInput = group.eev ?? "";
          const eevNumber = parseInt(eevInput, 10);
          const recommendedEev = totalHp > 0 ? Math.ceil(totalHp / 10) : 0;
          return {
            groupNo: index + 1,
            unit,
            circuitNos,
            totalHp,
            totalKw,
            eevInput,
            eevNumber,
            recommendedEev,
            hasEev: eevInput !== "" && Number.isFinite(eevNumber),
            isValid: Boolean(unit) && circuitNos.length > 0,
          };
        })
        .filter((group) => group.isValid);

      const uniqueCircuitNos = [...new Set(appliedSharedGroups.flatMap((group) => group.circuitNos))].sort((a, b) => a - b);
      appliedTotalHp = appliedSharedGroups.reduce((sum, group) => sum + group.totalHp, 0);
      appliedTotalKw = appliedTotalHp * 3.5;
      appliedCircuitCount = uniqueCircuitNos.length;
      appliedTotalEev = appliedSharedGroups.reduce((sum, group) => sum + (Number.isFinite(group.eevNumber) ? group.eevNumber : 0), 0);
      hasSharedMultiCircuit = appliedSharedGroups.some((group) => group.circuitNos.length > 1);

      appliedCircuits = uniqueCircuitNos.map((circuitNo) => {
        const connectedGroups = appliedSharedGroups.filter((group) => group.circuitNos.includes(circuitNo));
        return {
          circuitNo,
          units: connectedGroups.map((group) => ({ hp: group.totalHp, model: group.unit?.model || "-", sharedGroupNo: group.groupNo })),
          totalHp: connectedGroups.reduce((sum, group) => sum + group.totalHp, 0),
          totalKw: connectedGroups.reduce((sum, group) => sum + group.totalKw, 0),
          eevNumber: 0,
          recommendedEev: Math.ceil((expectedCircuitHpByNo[circuitNo] || 0) / 10) || 0,
          hasEev: true,
          isShared: true,
        };
      });

      missingEevCircuits = appliedSharedGroups.filter((group) => !group.hasEev);
      wrongEevCircuits = appliedSharedGroups.filter((group) => group.hasEev && group.eevNumber !== group.recommendedEev);
      appliedCircuitText = appliedCircuits.length > 0
        ? appliedCircuits.map((circuit) => `Devre ${circuit.circuitNo}: ${circuit.units.map((unit) => `Ortak Grup ${unit.sharedGroupNo}: ${unit.model} - ${unit.hp} HP`).join(" + ")}`).join(" | ")
        : "-";
      appliedModelText = appliedSharedGroups.length > 0
        ? appliedSharedGroups.map((group) => `Ortak Grup ${group.groupNo}: ${group.unit.model} - ${group.totalHp} HP → bağlı devreler: ${group.circuitNos.map((circuitNo) => `Devre ${circuitNo}`).join(", ")}`).join(" | ")
        : "-";
    } else {
      appliedCircuits = ahuFieldCircuits
        .map((circuit, index) => {
          const totalHp = circuit.units.reduce((sum, unit) => sum + (Number(unit.hp) || 0), 0);
          const totalKw = totalHp * 3.5;
          const eevNumber = parseInt(circuit.eev, 10);
          const recommendedEev = totalHp > 0 ? Math.ceil(totalHp / 10) : 0;
          return { circuitNo: index + 1, units: circuit.units, totalHp, totalKw, eevInput: circuit.eev, eevNumber, recommendedEev, hasEev: circuit.eev !== "" && Number.isFinite(eevNumber), isShared: false };
        })
        .filter((circuit) => circuit.units.length > 0);
      appliedTotalHp = appliedCircuits.reduce((sum, circuit) => sum + circuit.totalHp, 0);
      appliedTotalKw = appliedTotalHp * 3.5;
      appliedCircuitCount = appliedCircuits.length;
      appliedTotalEev = appliedCircuits.reduce((sum, circuit) => sum + (Number.isFinite(circuit.eevNumber) ? circuit.eevNumber : 0), 0);
      appliedCircuitText = appliedCircuits.length > 0 ? appliedCircuits.map((circuit) => `Devre ${circuit.circuitNo}: ${formatNumber(circuit.totalHp)} HP / ${formatNumber(circuit.totalKw)} kW`).join(" | ") : "-";
      appliedModelText = appliedCircuits.length > 0 ? appliedCircuits.map((circuit) => `Devre ${circuit.circuitNo}: ${circuit.units.map((unit, unitIndex) => `${unitIndex + 1}. ${unit.model} - ${unit.hp} HP`).join(" + ")}`).join(" | ") : "-";
      sameCircuitDistribution = hasExpectedSelection && appliedCircuitCount === expectedCircuitCount && appliedCircuits.every((circuit) => Math.abs((expectedCircuitHpByNo[circuit.circuitNo] || 0) - circuit.totalHp) <= 0.01);
      const maxUnits = getMaxFieldUnitsPerCircuit(ahuFieldOutdoorMode);
      limitIssues = appliedCircuits.filter((circuit) => circuit.units.length > maxUnits);
      missingEevCircuits = appliedCircuits.filter((circuit) => !circuit.hasEev);
      wrongEevCircuits = appliedCircuits.filter((circuit) => circuit.hasEev && circuit.eevNumber !== circuit.recommendedEev);
    }

    const fieldLimitDescription = ahuFieldOutdoorMode === "Eco" ? "Eco modda her devrede maksimum 1 Eco dış ünite seçilebilir." : "Standart modda her devrede maksimum 4 dış ünite seçilebilir.";
    const connectionTypeLabel = isSharedConnection ? "Ortak dış ünite / çoklu devre bağlantısı" : "Her devreye ayrı dış ünite bağlantısı";
    const hasAppliedSelection = isSharedConnection ? appliedSharedGroups.length > 0 : appliedCircuits.length > 0;
    const sharedConnectionReason = hasSharedMultiCircuit ? FIELD_SHARED_CONNECTION_WARNING : isSharedConnection ? "Ortak bağlantı seçildi; fiziksel dış ünite HP'si yalnızca bir kez sayılır." : "Her devre ayrı dış ünite seçimiyle değerlendiriliyor.";

    const perCircuitHpLimit = ahuFieldOutdoorMode === "Eco" ? 12 : 40;
    const circuitHpLimitIssues = !isSharedConnection
      ? appliedCircuits.filter((circuit) => circuit.totalHp > perCircuitHpLimit)
      : [];
    const circuitBalanceOk =
      !isSharedConnection &&
      hasExpectedSelection &&
      hasAppliedSelection &&
      appliedCircuitCount === expectedCircuitCount &&
      appliedCircuits.every((circuit) => Math.abs((expectedCircuitHpByNo[circuit.circuitNo] || 0) - circuit.totalHp) <= 0.01);

    const appliedMinAirflow = hasAppliedSelection ? appliedTotalHp * 432 : 0;
    const appliedStdAirflow = hasAppliedSelection ? appliedTotalHp * 480 : 0;
    const appliedMaxAirflow = hasAppliedSelection ? appliedTotalHp * 576 : 0;
    const hasFieldAirflow = Boolean(ahuCalc?.hasAirflow);
    const fieldAirflowM3h = hasFieldAirflow ? Number(ahuCalc?.resolvedAirflowM3h || 0) : 0;
    const appliedAirflowOk =
      hasAppliedSelection &&
      hasFieldAirflow &&
      fieldAirflowM3h >= appliedMinAirflow &&
      fieldAirflowM3h <= appliedMaxAirflow;

    const appliedAirflowAppliedText = !hasAppliedSelection
      ? "Sahada uygulanan dış ünite seçilmedi"
      : hasFieldAirflow
      ? `Girilen debi ${formatNumber(fieldAirflowM3h)} m³/h / Sahada uygulanan ${formatNumber(appliedTotalHp)} HP dış ünite aralığı: Min ${formatNumber(appliedMinAirflow)} / Standart ${formatNumber(appliedStdAirflow)} / Max ${formatNumber(appliedMaxAirflow)} m³/h`
      : `Debi girilmedi / Sahada uygulanan ${formatNumber(appliedTotalHp)} HP dış ünite aralığı: Min ${formatNumber(appliedMinAirflow)} / Standart ${formatNumber(appliedStdAirflow)} / Max ${formatNumber(appliedMaxAirflow)} m³/h`;

    const appliedAirflowReason = !hasAppliedSelection
      ? "Sahada uygulanan dış ünite seçimi yapılmalıdır."
      : !hasFieldAirflow
      ? "Santral debisi girilmediği için saha dış ünitesine göre debi kontrolü yapılamaz."
      : appliedAirflowOk
      ? `Girilen ${formatNumber(fieldAirflowM3h)} m³/h debi, sahada uygulanan ${formatNumber(appliedTotalHp)} HP dış ünite için ${formatNumber(appliedMinAirflow)} – ${formatNumber(appliedMaxAirflow)} m³/h aralığındadır.`
      : `Girilen ${formatNumber(fieldAirflowM3h)} m³/h debi, sahada uygulanan ${formatNumber(appliedTotalHp)} HP dış ünitenin ${formatNumber(appliedMinAirflow)} – ${formatNumber(appliedMaxAirflow)} m³/h aralığı dışında kalıyor.`;

    const coreTechnicalLabels = [
      "Proje Adı",
      "Servis Adı",
      "Tarih",
      "Toplam Soğutma Kapasitesi",
      "Santral Debisi",
      "Evaporasyon Sıcaklığı",
      "Superheat",
      "Subcool",
      "Kondenzasyon Sıcaklığı",
      "Batarya Giriş Havası Sıcaklığı",
      "R410A Basınç Kaybı",
      "Hava Hızı",
    ];
    const coreTechnicalFailedRows = (ahuCalc?.validationRows || []).filter(
      (row) => coreTechnicalLabels.includes(row.label) && row.ok !== true
    );
    const appliedTechnicalOk =
      hasAppliedSelection &&
      appliedAirflowOk &&
      coreTechnicalFailedRows.length === 0;
    const appliedTechnicalReason = !hasAppliedSelection
      ? "Sahada uygulanan dış ünite seçimi yapılmadığı için teknik kontrol tamamlanamaz."
      : coreTechnicalFailedRows.length > 0
      ? `Eksik / uygun olmayan girişler: ${coreTechnicalFailedRows
          .map((row) => `${row.label} ${row.value} (Kural: ${row.rule})`)
          .join(", ")}.`
      : !appliedAirflowOk
      ? appliedAirflowReason
      : "Teknik girişler ve saha dış ünitesine göre debi kontrolü uygun.";

    const rows = [
      { label: "Bağlantı Tipi", expected: "Her bağımsız DX devre kendi kapasite ve EEV kontrolüne göre değerlendirilmelidir.", applied: connectionTypeLabel, ok: !hasSharedMultiCircuit, reason: sharedConnectionReason },
      { label: "Dış Ünite Modu", expected: hasExpectedSelection ? ahuOutdoorMode : "-", applied: ahuFieldOutdoorMode || "-", ok: hasExpectedSelection && ahuFieldOutdoorMode === ahuOutdoorMode, reason: hasExpectedSelection && ahuFieldOutdoorMode !== ahuOutdoorMode ? `Hesaplanan mod ${ahuOutdoorMode}, sahada uygulanan mod ${ahuFieldOutdoorMode}.` : "Mod uyumu kontrol edilir." },
      { label: "Sahada Uygulanan Model / Kombinasyon", expected: hasExpectedSelection ? expectedCircuitText : "-", applied: appliedModelText, ok: hasExpectedSelection && hasAppliedSelection, reason: !hasAppliedSelection ? "Sahada uygulanan dış ünite seçimi yapılmalıdır." : "Sahada uygulanan modeller bağlantı tipine göre kayıt altına alınır." },
      { label: "Dış Ünite Adedi Limiti", expected: isSharedConnection ? "Ortak bağlantıda her ortak grupta 1 fiziksel dış ünite seçilir." : fieldLimitDescription, applied: hasAppliedSelection ? (isSharedConnection ? appliedSharedGroups.map((group) => `Ortak Grup ${group.groupNo}: 1 dış ünite / ${group.circuitNos.length} devre`).join(" | ") : appliedCircuits.map((circuit) => `Devre ${circuit.circuitNo}: ${circuit.units.length} adet`).join(" | ")) : "-", ok: hasAppliedSelection && (isSharedConnection || limitIssues.length === 0), reason: !hasAppliedSelection ? "Sahada uygulanan dış ünite seçimi yapılmalıdır." : !isSharedConnection && limitIssues.length > 0 ? `${limitIssues.map((circuit) => `Devre ${circuit.circuitNo}`).join(", ")} cihaz adedi limitini aşıyor.` : isSharedConnection ? "Fiziksel dış ünite ortak grupta bir kez sayılır; bağlı olduğu devreler ayrı işaretlenir." : fieldLimitDescription },
      { label: "Toplam Dış Ünite HP", expected: hasExpectedSelection ? `${formatNumber(expectedHp)} HP` : "-", applied: hasAppliedSelection ? `${formatNumber(appliedTotalHp)} HP` : "-", ok: hasExpectedSelection && hasAppliedSelection && Math.abs(appliedTotalHp - expectedHp) <= 0.01, reason: !hasAppliedSelection ? "Sahada uygulanan dış ünite seçimi yapılmalıdır." : appliedTotalHp < expectedHp ? `Sahada uygulanan ${formatNumber(appliedTotalHp)} HP, olması gereken ${formatNumber(expectedHp)} HP değerinden düşük.` : appliedTotalHp > expectedHp ? `Sahada uygulanan ${formatNumber(appliedTotalHp)} HP, olması gereken ${formatNumber(expectedHp)} HP seçiminden farklı / yüksek.` : "Toplam HP olması gereken seçimle aynı." },
      { label: "Toplam Dış Ünite kW", expected: hasExpectedSelection ? `${formatNumber(expectedKw)} kW` : "-", applied: hasAppliedSelection ? `${formatNumber(appliedTotalKw)} kW` : "-", ok: hasExpectedSelection && hasAppliedSelection && Math.abs(appliedTotalHp - expectedHp) <= 0.01, reason: "kW karşılığı sahada uygulanan HP × 3,5 olarak değerlendirilir." },
      { label: "DX Devre Sayısı", expected: hasExpectedSelection ? `${expectedCircuitCount}` : "-", applied: hasAppliedSelection ? `${appliedCircuitCount}` : "-", ok: hasExpectedSelection && appliedCircuitCount === expectedCircuitCount, reason: !hasAppliedSelection ? "Sahada kullanılan devre seçimi yapılmalıdır." : appliedCircuitCount < expectedCircuitCount ? `Olması gereken devre sayısı ${expectedCircuitCount}, sahada uygulanan ${appliedCircuitCount}. Devre başına HP sınırı nedeniyle devre eklenmelidir.` : appliedCircuitCount > expectedCircuitCount ? `Olması gereken devre sayısı ${expectedCircuitCount}, sahada uygulanan ${appliedCircuitCount}. Devre sayısı olması gereken plana göre dengelenmelidir.` : "Devre sayısı uyumlu." },
      { label: "Devre Başı HP Sınırı", expected: ahuFieldOutdoorMode === "Eco" ? "Eco: devre başı en fazla 12 HP" : "Standart: devre başı en fazla 40 HP", applied: hasAppliedSelection ? appliedCircuits.map((circuit) => `Devre ${circuit.circuitNo}: ${formatNumber(circuit.totalHp)} HP`).join(" | ") : "-", ok: isSharedConnection ? !hasSharedMultiCircuit : hasAppliedSelection && circuitHpLimitIssues.length === 0, reason: !hasAppliedSelection ? "Sahada uygulanan devre seçimi yapılmalıdır." : isSharedConnection && hasSharedMultiCircuit ? "Tek gövde dış ünite birden fazla bağımsız devreyi ortak beslediği için uygun kabul edilmez." : circuitHpLimitIssues.length > 0 ? `${circuitHpLimitIssues.map((circuit) => `Devre ${circuit.circuitNo}: ${formatNumber(circuit.totalHp)} HP`).join(", ")}. Devre başı ${perCircuitHpLimit} HP sınırı aşılıyor; devre eklenmelidir.` : "Devre başı HP sınırı sağlandı." },
      { label: "Devre Bazlı HP Dağılımı", expected: hasExpectedSelection ? expectedCircuitText : "-", applied: appliedCircuitText, ok: !isSharedConnection && circuitBalanceOk, reason: !hasAppliedSelection ? "Sahada uygulanan devreler seçilmelidir." : isSharedConnection && hasSharedMultiCircuit ? "Ortak dış ünite bağlantısında tek gövde dış ünite birden fazla devreyi beslediği için devre bazlı HP dağılımı uygun kabul edilmez." : isSharedConnection ? "Ortak bağlantı seçildiği için devre bazlı HP dağılımı ayrıca kontrol edilmelidir." : !circuitBalanceOk ? "Sahadaki devre bazlı HP dağılımı, olması gereken devre dağılımıyla dengeli / aynı değil." : "Devre bazlı HP dağılımı uyumlu." },
      { label: "Toplam EEV", expected: hasExpectedSelection ? `${expectedEev} adet` : "-", applied: hasAppliedSelection ? `${appliedTotalEev} adet` : "-", ok: hasExpectedSelection && hasAppliedSelection && appliedTotalEev === expectedEev, reason: !hasAppliedSelection ? "Sahada uygulanan devre seçimi yapılmalıdır." : appliedTotalEev !== expectedEev ? `Olması gereken toplam EEV ${expectedEev} adet, sahada uygulanan ${appliedTotalEev} adet.` : "Toplam EEV adedi uyumlu." },
      isSharedConnection
        ? { label: "Ortak Grup EEV", expected: "Ortak dış ünite bağlantısında EEV devre devre ayrılmaz; fiziksel dış ünite grubu için tek kullanılan EEV adedi girilir.", applied: hasAppliedSelection ? appliedSharedGroups.map((group) => `Ortak Grup ${group.groupNo}: ${group.hasEev ? `${group.eevNumber} EEV` : "EEV girilmedi"}`).join(" | ") : "-", ok: hasAppliedSelection && missingEevCircuits.length === 0 && wrongEevCircuits.length === 0, reason: missingEevCircuits.length > 0 ? `${missingEevCircuits.map((group) => `Ortak Grup ${group.groupNo}`).join(", ")} için sahada kullanılan toplam EEV girilmelidir.` : wrongEevCircuits.length > 0 ? `${wrongEevCircuits.map((group) => `Ortak Grup ${group.groupNo}: girilen ${group.eevNumber}, tek gövde önerisi ${group.recommendedEev}`).join(" | ")}.` : "Ortak dış ünite gruplarında EEV adedi tek alan olarak değerlendirildi." }
        : { label: "Devre Bazlı EEV", expected: "Her devrede EEV = devre HP / 10 yukarı yuvarlama", applied: hasAppliedSelection ? appliedCircuits.map((circuit) => `Devre ${circuit.circuitNo}: ${circuit.hasEev ? `${circuit.eevNumber} EEV` : "EEV girilmedi"}`).join(" | ") : "-", ok: hasAppliedSelection && missingEevCircuits.length === 0 && wrongEevCircuits.length === 0, reason: missingEevCircuits.length > 0 ? `${missingEevCircuits.map((circuit) => `Devre ${circuit.circuitNo}`).join(", ")} için sahada kullanılan EEV girilmelidir.` : wrongEevCircuits.length > 0 ? `${wrongEevCircuits.map((circuit) => `Devre ${circuit.circuitNo}: girilen ${circuit.eevNumber}, önerilen ${circuit.recommendedEev}`).join(" | ")}.` : "Devre bazlı EEV adedi uygun." },
      { label: "Saha Dış Ünitesine Göre Debi Kontrolü", expected: hasAppliedSelection ? `Sahada uygulanan ${formatNumber(appliedTotalHp)} HP dış üniteye göre Min ${formatNumber(appliedMinAirflow)} / Standart ${formatNumber(appliedStdAirflow)} / Max ${formatNumber(appliedMaxAirflow)} m³/h` : "Sahada uygulanan dış ünite seçimi bekleniyor", applied: appliedAirflowAppliedText, ok: appliedAirflowOk, reason: appliedAirflowReason },
      { label: "Ana Teknik Kontrol", expected: "Saha karşılaştırmasında teknik kontrol, sahada girilen dış ünite HP değerine göre yapılır", applied: appliedTechnicalOk ? "UYGUN" : "KONTROL GEREKİYOR", ok: appliedTechnicalOk, reason: appliedTechnicalReason },
    ];

    if (hasSharedMultiCircuit) {
      rows.splice(1, 0, { label: "Tek Gövde Çoklu Devre Uyarısı", expected: "Her bağımsız DX devrenin soğutucu akışkan kontrolü ve EEV kapasitesi ayrı yönetilmelidir.", applied: appliedSharedGroups.filter((group) => group.circuitNos.length > 1).map((group) => `Ortak Grup ${group.groupNo}: ${group.unit.model} - ${group.totalHp} HP → ${group.circuitNos.map((circuitNo) => `Devre ${circuitNo}`).join(", ")}`).join(" | "), ok: false, reason: FIELD_SHARED_CONNECTION_WARNING });
    }

    const issues = rows.filter((row) => !row.ok).map((row) => `${row.label}: ${row.reason}`);
    const allOk = hasExpectedSelection && rows.every((row) => row.ok === true) && !hasSharedMultiCircuit;
    const appliedDisplayBlocks = isSharedConnection
      ? appliedSharedGroups.map((group) => ({
          title: `Ortak Dış Ünite Grubu ${group.groupNo}`,
          subtitle: `${group.unit.model} — ${formatNumber(group.totalHp)} HP / ${formatNumber(group.totalKw)} kW`,
          warning: group.circuitNos.length > 1 ? FIELD_SHARED_CONNECTION_WARNING : "",
          lines: [
            `Bağlı devreler: ${group.circuitNos.map((circuitNo) => `Devre ${circuitNo}`).join(", ")}`,
            `Sahada kullanılan EEV: ${group.hasEev ? group.eevNumber : "Girilmedi"}`,
            "Not: Ortak bağlantıda EEV devre bazında ayrılmadan, fiziksel dış ünite grubu için tek toplam değer olarak kaydedilir.",
          ],
        }))
      : appliedCircuits.map((circuit) => ({ title: `Devre ${circuit.circuitNo}`, subtitle: `${formatNumber(circuit.totalHp)} HP / ${formatNumber(circuit.totalKw)} kW`, warning: "", lines: [...circuit.units.map((unit, unitIndex) => `${unitIndex + 1}. Ünite — ${unit.model} — ${unit.hp} HP`), `EEV: Sahada ${circuit.hasEev ? circuit.eevNumber : "Girilmedi"}`] }));

    return { enabled: ahuFieldCompareEnabled, connectionType: ahuFieldConnectionType, connectionTypeLabel, isSharedConnection, hasSharedMultiCircuit, hasExpectedSelection, expectedCircuitGroups, appliedCircuits, appliedSharedGroups, appliedDisplayBlocks, appliedTotalHp, appliedTotalKw, appliedCircuitCount, appliedTotalEev, appliedMinAirflow, appliedStdAirflow, appliedMaxAirflow, appliedAirflowOk, expectedHp, expectedKw, expectedCircuitCount, expectedEev, expectedMinAirflow, expectedStdAirflow, expectedMaxAirflow, expectedCircuitText, appliedCircuitText, appliedModelText, rows, issues, allOk, summary: allOk ? "Sahada uygulanan seçim, olması gereken hesapla uyumlu." : issues.length > 0 ? issues.join(" ") : "Saha karşılaştırması için eksik bilgi var." };
  }, [ahuFieldCompareEnabled, ahuFieldOutdoorMode, ahuFieldConnectionType, ahuFieldCircuits, ahuFieldSharedGroups, ahuCalc, ahuOutdoorMode]);

  const goToFinalStep = ({
    finalTitle,
    finalText,
    actions = [],
    path = "",
    detailRows = [],
  }) => {
    setFlowResult(finalText);
    setFlowSummary({
      finalTitle,
      finalText,
      actions,
      path,
      detailRows,
    });
    setStep(13);
  };

  const finalizeTechnicalSupport = () =>
    goToFinalStep({
      finalTitle: "Teknik Destek Yönlendirmesi",
      finalText:
        "Kompresör içerisinde mekanik arıza şüphesi bulunduğundan işlem teknik destek değerlendirmesi ile devam etmelidir.",
      actions: [
        "Kompresör çalıştırılmamalıdır.",
        "Sistem üzerinde ek işlem uygulanmadan teknik destek birimi bilgilendirilmelidir.",
        "Teknik destek onayı sonrası işlem planı oluşturulmalıdır.",
      ],
      path: "Mekanik Ses Tespit Edildi → Teknik Destek Birimine Danış",
      detailRows: [],
    });

  const finalizeValveReplacement = () =>
    goToFinalStep({
      finalTitle: "Parça Değişimi Gerekli",
      finalText:
        "ARV / yağ valfi veya yağ separatöründe sorun tespit edildi. Kompresör değişimi öncesinde ilgili parçaların değişimi tamamlanmalıdır.",
      actions: [
        "ARV / yağ valfi kontrol edilerek arızalı ise değiştirilmelidir.",
        "Yağ separatörü kontrol edilerek gerekiyorsa değiştirilmelidir.",
        "Parça değişimi tamamlandıktan sonra kompresör değişimine geçilmelidir.",
      ],
      path: "Mekanik Ses Yok → 800 gr Ağır → ARV / Yağ Separatör Sorunlu",
      detailRows: [],
    });

  const finalizeOilTrap = () =>
    goToFinalStep({
      finalTitle: "Yağ Dolaşım KontrolÜ Gerekli",
      finalText:
        "ARV / yağ separatör hattında yağ çıkışı gözlenmedi. Sistemde yağ dolaşımı ve yağ kapanı kontrol edilerek ilave yağ işlemi uygulanmalıdır.",
      actions: [
        "Bakır tesisatta yağ kapanı olup olmadığı kontrol edilmelidir.",
        "Sisteme 5 litre yeni yağ eklenmelidir.",
        "Yağ işlemi tamamlandıktan sonra kompresör değişimine geçilmelidir.",
      ],
      path: "Mekanik Ses Yok → 800 gr Ağır → ARV / Yağ Separatör Sorunsuz → Yağ Çıkışı Yok",
      detailRows: [],
    });

  const finalizeFullOilRenewal800 = () =>
    goToFinalStep({
      finalTitle: "Sistem Yağı Yenilenmeli",
      finalText:
        "ARV / yağ separatör hattından alınan yağ örneğinin rengi uygun bulunmadı. Sistem yağının tamamen yenilenmesi gerekmektedir.",
      actions: [
        "Sistem içerisindeki mevcut yağ tamamen boşaltılmalıdır.",
        "Sisteme yeniden 5 litre yeni yağ eklenmelidir.",
        "Yağ yenileme işlemi tamamlandıktan sonra kompresör değişimine geçilmelidir.",
      ],
      path: "Mekanik Ses Yok → 800 gr Ağır → ARV / Yağ Separatör Sorunsuz → Yağ Çıkışı Var → Yağ Rengi Uygun Değil",
      detailRows: [],
    });

  const finalizeDirectCompressorChange800 = () =>
        goToFinalStep({
      finalTitle: "Kompresör Değişimi Uygun",
      finalText:
        "Kontrol edilen yağ durumu uygun bulundu. Kompresör değişimi standart iş akışına göre uygulanabilir.",
      actions: [
        "Kompresör değişimine geçilebilir.",
        "Montaj sonrası standart devreye alma kontrolleri tamamlanmalıdır.",
      ],
      path: "Mekanik Ses Yok → 800 gr Ağır → ARV / Yağ Separatör Sorunsuz → Yağ Çıkışı Var → Yağ Rengi Uygun",
      detailRows: [],
    });

  const finalizeDirectCompressorChangeNormal = () =>
    goToFinalStep({
      finalTitle: "Kompresör Değişimi Uygun",
      finalText:
        "Sökülen kompresörde yağ rengi uygun bulundu. Sistem genelinde yağ yenileme gerekliliği görülmedi.",
      actions: [
        "Kompresör değişimine geçilebilir.",
        "Montaj sonrası standart devreye alma kontrolleri tamamlanmalıdır.",
      ],
      path: "Mekanik Ses Yok → Ağırlık Normal → Yağ Rengi Uygun",
      detailRows: buildCommonDetailRows(),
    });

  const finalizeLocalIssue = () =>
    goToFinalStep({
      finalTitle: "Lokal Arıza Senaryosu",
      finalText:
        "Sökülen kompresörde yağ rengi uygun bulunmadı ancak diğer kompresörler ve akümülatör tarafında sistemsel yağ bozulması görülmedi. İşlem lokal arıza yaklaşımı ile sürdürülebilir.",
      actions: [
        "Kompresör değişimine geçilebilir.",
        "Sistem genel yağ değişimi gerekli değildir.",
        "Montaj sonrası çalışma değerleri gözlemlenmelidir.",
      ],
      path: "Mekanik Ses Yok → Ağırlık Normal → Ana Yağ Rengi Uygun Değil → Diğer Kompresörler / Akümülatör Uygun",
      detailRows: buildCommonDetailRows(),
    });

  const finalizeFullOilRenewalNormal = () =>
    goToFinalStep({
      finalTitle: "Sistem Genelinde Yağ Yenilenmeli",
      finalText:
        "Diğer kompresörler ve / veya akümülatör tarafında da yağ renginin uygun olmadığı görüldü. Sistem genelinde yağ yenileme işlemi uygulanmalıdır.",
      actions: [
        "Sistem içerisindeki mevcut yağ tamamen boşaltılmalıdır.",
        "Sisteme yeni yağ verilmelidir.",
        "Gerekli temizlik ve kontrol işlemleri sonrası kompresör değişimine geçilmelidir.",
      ],
      path: "Mekanik Ses Yok → Ağırlık Normal → Ana Yağ Rengi Uygun Değil → Diğer Kompresörler / Akümülatör Uygun Değil",
      detailRows: buildCommonDetailRows(),
    });

  const goNext = () => {
    if (step === 1) {
      const ok = requireFields([
        {
          valid: serviceCompany.trim() !== "",
          message: "Servis Adı / Firma bilgisini girmek zorundasın.",
        },
        {
          valid: servicePerson.trim() !== "",
          message: "Servis Personeli bilgisini girmek zorundasın.",
        },
        {
          valid: reportDate.trim() !== "",
          message: "Tarih bilgisini girmek zorundasın.",
        },
        {
          valid: customerName.trim() !== "",
          message: "Müşteri Adı bilgisini girmek zorundasın.",
        },
      ]);
      if (!ok) return;
      setStep(2);
      return;
    }

    if (step === 2) {
      const ok = requirePhotos(
        ["modelSerialLabel"],
        "Cihaz model etiketi fotoğrafını yüklemek zorundasın."
      );
      if (!ok) return;
      setStep(3);
      return;
    }

    if (step === 3) {
      const ok = requirePhotos(
        ["oldCompLabel", "newCompLabel"],
        "Eski ve yeni kompresör model etiketi fotoğraflarını yüklemek zorundasın."
      );
      if (!ok) return;
      setStep(4);
      return;
    }

    if (step === 4) {
      const ok = requireFields([
        {
          valid: selectedModel !== "",
          message: "Kompresör modelini seçmek zorundasın.",
        },
        {
          valid: temperature !== "" && !Number.isNaN(parseFloat(temperature)),
          message: "Geçerli bir sıcaklık girmek zorundasın.",
        },
        {
          valid: measuredUW !== "" && !Number.isNaN(parseMilliOhmInput(measuredUW)),
          message: "U-W ölçüm değerini girmek zorundasın.",
        },
        {
          valid: measuredUV !== "" && !Number.isNaN(parseMilliOhmInput(measuredUV)),
          message: "U-V ölçüm değerini girmek zorundasın.",
        },
        {
          valid: measuredWV !== "" && !Number.isNaN(parseMilliOhmInput(measuredWV)),
          message: "W-V ölçüm değerini girmek zorundasın.",
        },
      ]);
      if (!ok) return;

      const photoOk = requirePhotos(
        ["mohmUW", "mohmUV", "mohmWV"],
        "mΩ ölçüm fotoğraflarının 3'ünü de yüklemek zorundasın."
      );
      if (!photoOk) return;

      setStep(5);
      return;
    }

    if (step === 5) {
      const ok = requirePhotos(
        ["oldCompWeight", "newCompWeight"],
        "Eski ve yeni kompresör terazi fotoğraflarını yüklemek zorundasın."
      );
      if (!ok) return;
      setStep(6);
    }
  };

  const goBack = () => {
    if (step === 1) return;

    if (step === 13) {
      if (mechanicalSound === "evet") {
        setStep(6);
        return;
      }

      if (weightStatus === "800gr-ağır") {
        if (valveSeparatorIssue === "evet") {
          setStep(8);
          return;
        }
        if (oilCameFromValves === "hayir") {
          setStep(9);
          return;
        }
        if (oilColorAfterValve) {
          setStep(10);
          return;
        }
      }

      if (weightStatus === "eşit-veya-eski-daha-ağır") {
        if (otherCompressorsOilSuitable) {
          setStep(12);
          return;
        }
        if (mainOilColorSuitable) {
          setStep(11);
          return;
        }
      }

      setStep(6);
      return;
    }

    setStep((prev) => prev - 1);
  };

  const dataUrlFormat = (dataUrl = "") => {
    if (dataUrl.startsWith("data:image/png")) return "PNG";
    if (dataUrl.startsWith("data:image/webp")) return "WEBP";
    return "JPEG";
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    const chunkSize = 0x8000;

    for (let i = 0; i < len; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }

    return window.btoa(binary);
  };

  const loadPdfFont = async (doc, url, vfsName, family, style) => {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Font yüklenemedi: ${url}`);
    }

    const buffer = await res.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    doc.addFileToVFS(vfsName, base64);
    doc.addFont(vfsName, family, style);
  };

  const setupPdfFonts = async (doc) => {
    try {
      await loadPdfFont(
        doc,
        "/fonts/SamsungSans-Regular.ttf",
        "SamsungSans-Regular.ttf",
        "SamsungSans",
        "normal"
      );

      await loadPdfFont(
                doc,
        "/fonts/SamsungSans-Bold.ttf",
        "SamsungSans-Bold.ttf",
        "SamsungSans",
        "bold"
      );

      doc.setFont("SamsungSans", "normal");
      return true;
    } catch (e) {
      console.error("SamsungSans yüklenemedi, Helvetica fallback:", e);
      doc.setFont("helvetica", "normal");
      return false;
    }
  };

  const setPdfFont = (doc, fontOk, style = "normal") => {
    if (fontOk) {
      doc.setFont("SamsungSans", style);
    } else {
      doc.setFont("helvetica", style);
    }
  };

  const loadImageAsDataURL = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Logo yüklenemedi");
    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const addPdfHeader = async (doc, fontOk, title = "Kompresör Kontrol Formu") => {
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, 210, 18, "F");

    try {
      const logoDataUrl = await loadImageAsDataURL(samsungLogo);
            doc.addImage(logoDataUrl, dataUrlFormat(logoDataUrl), 8, 5, 28, 6);
    } catch (e) {
      console.log("Logo yüklenemedi, logosuz devam.");
    }

    doc.setTextColor(255, 255, 255);
    setPdfFont(doc, fontOk, "bold");
    doc.setFontSize(15);
    doc.text(title, 105, 11, { align: "center" });

    doc.setTextColor(220, 220, 220);
    setPdfFont(doc, fontOk, "normal");
    doc.setFontSize(9);
    doc.text("Klima Sistemleri", 198, 11, { align: "right" });

    doc.setTextColor(0, 0, 0);
  };

  const ensurePdfSpace = async (
    doc,
    fontOk,
    y,
    neededHeight,
    title = "AHU Kontrol",
    bottomLimit = 280
  ) => {
    if (y + neededHeight > bottomLimit) {
      doc.addPage();
      await addPdfHeader(doc, fontOk, title);
      return 24;
    }
    return y;
  };

  const drawPhotoBlock = (doc, fontOk, title, image, x, y, w = 80, h = 55) => {
    setPdfFont(doc, fontOk, "bold");
    doc.setFontSize(12);
    doc.text(title, x, y);

    doc.setDrawColor(180);
    doc.roundedRect(x, y + 4, w, h, 3, 3);

    if (image) {
      try {
        doc.addImage(image, dataUrlFormat(image), x + 2, y + 6, w - 4, h - 4);
      } catch (e) {
        console.log("Foto eklenemedi:", title, e);
      }
    }
  };

  const drawReferenceBlock = (doc, fontOk, title, image, x, y, w = 80, h = 45) => {
    setPdfFont(doc, fontOk, "bold");
    doc.setFontSize(10);
    doc.text(title, x, y);

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(200, 210, 220);
    doc.roundedRect(x, y + 4, w, h, 3, 3, "FD");

    if (image) {
      try {
        doc.addImage(image, dataUrlFormat(image), x + 2, y + 6, w - 4, h - 4);
      } catch (e) {
        console.log("Referans görsel eklenemedi:", title, e);
      }
    }
  };

  const handlePhotoChange = (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotos((prev) => ({
        ...prev,
        [key]: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const requirePhotos = (keys, message) => {
    const missing = keys.some((key) => !photos[key]);
    if (missing) {
      alert(message);
      return false;
    }
    return true;
  };

  const requireFields = (checks) => {
    for (const check of checks) {
      if (!check.valid) {
        alert(check.message);
        return false;
      }
    }
    return true;
  };

  const buildCommonDetailRows = (extra = []) => {
    const rows = [];

    if (mechanicalSound) {
      rows.push({
        label: "Mekanik Ses KontrolÜ",
        value:
          mechanicalSound === "evet"
            ? "Mekanik ses tespit edildi"
            : "Mekanik ses tespit edilmedi",
      });
    }

    if (weightStatus) {
      rows.push({
        label: "Ağırlık Değerlendirmesi",
        value:
          weightStatus === "800gr-ağır"
            ? "Yeni kompresör, sökülen kompresörden 800 gram daha ağır"
            : "Sökülen kompresör, yenisiyle aynı ağırlıkta veya daha ağır",
      });
    }

    if (valveSeparatorIssue) {
      rows.push({
        label: "ARV / Yağ Separatör KontrolÜ",
        value:
          valveSeparatorIssue === "evet"
            ? "ARV / yağ separatörde sorun tespit edildi"
            : "ARV / yağ separatörde sorun tespit edilmedi",
      });
    }

    if (oilCameFromValves) {
      rows.push({
        label: "Yağ Çıkış KontrolÜ",
        value:
          oilCameFromValves === "evet"
            ? "Yağ çıkışı gözlendi"
            : "Yağ çıkışı gözlenmedi",
      });
    }

    if (oilColorAfterValve) {
      rows.push({
        label: "ARV / Separatör Yağ Renk KontrolÜ",
        value:
          oilColorAfterValve === "evet"
            ? "Çıkan yağın rengi uygun bulundu"
            : "Çıkan yağın rengi uygun bulunmadı",
      });
    }

    if (mainOilColorSuitable) {
      rows.push({
        label: "Sökülen Kompresör Yağ Renk KontrolÜ",
        value:
          mainOilColorSuitable === "evet"
            ? "Yağ rengi uygun bulundu"
            : "Yağ rengi uygun bulunmadı",
      });
    }

    if (otherCompressorsOilSuitable) {
      rows.push({
        label: "Diğer Kompresörler ve Akümülatör Yağ KontrolÜ",
        value:
          otherCompressorsOilSuitable === "evet"
            ? "Diğer kompresörler / akümülatör yağı uygun bulundu"
            : "Diğer kompresörler / akümülatör yağı uygun bulunmadı",
      });
    }

    return [...rows, ...extra];
  };

  const generatePDF = async () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const fontOk = await setupPdfFonts(doc);

      await addPdfHeader(doc, fontOk, "Kompresör Kontrol Formu");

      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(18);
      doc.text("Genel Bilgiler", 105, 38, { align: "center" });

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(11);
      doc.setTextColor(100, 116, 139);
      doc.text("İşlem öncesi temel kayıt bilgileri", 105, 46, { align: "center" });
      doc.setTextColor(0, 0, 0);

      const boxX = 30;
      const boxY = 58;
      const boxW = 150;
      const boxH = 78;

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(boxX, boxY, boxW, boxH, 6, 6, "FD");

      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(13);
      doc.text("Kayıt Bilgileri", 105, 70, { align: "center" });

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(11);
      const leftX = boxX + 12;
      let lineY = 84;
      const lineGap = 12;
      doc.text(`Servis Adı / Firma: ${serviceCompany}`, leftX, lineY);
      lineY += lineGap;
      doc.text(`Servis Personeli: ${servicePerson}`, leftX, lineY);
      lineY += lineGap;
      doc.text(`Tarih: ${reportDate}`, leftX, lineY);
      lineY += lineGap;
      doc.text(`Müşteri Adı: ${customerName}`, leftX, lineY);

      doc.addPage();
      await addPdfHeader(doc, fontOk, "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(16);
      doc.text("Cihaz Model Etiketi", 14, 30);
      drawPhotoBlock(doc, fontOk, "Yüklenen Fotoğraf", photos.modelSerialLabel, 14, 42, 182, 78);
      drawReferenceBlock(doc, fontOk, "Sabit Referans Görsel", sampleProductLabel, 14, 128, 182, 90);

      doc.addPage();
      await addPdfHeader(doc, fontOk, "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(16);
      doc.text("Kompresör Model Etiketi", 14, 30);
      drawPhotoBlock(doc, fontOk, "Eski Kompresör Model Etiketi", photos.oldCompLabel, 14, 42, 86, 58);
      drawPhotoBlock(doc, fontOk, "Yeni Kompresör Model Etiketi", photos.newCompLabel, 110, 42, 86, 58);
      drawReferenceBlock(doc, fontOk, "Sabit Referans Görsel", sampleCompressorLabel, 14, 112, 182, 70);

      doc.addPage();
      await addPdfHeader(doc, fontOk, "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(16);
      doc.text("Kompresör mΩ Değerleri", 14, 30);

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(11);
      doc.text(`Model: ${selectedModel}`, 14, 42);
      doc.text(`Sıcaklık: ${temperature} °C`, 14, 50);
      doc.text(`Beklenen Değer: ${formatMilliOhm(expectedResistance)} mΩ`, 14, 58);
      doc.text(
        `Uygun Aralık: ${formatMilliOhm(minResistance)} - ${formatMilliOhm(maxResistance)} mΩ`,
        14,
        66
      );
      doc.text(`U-W: ${formatMilliOhmInput(measuredUW)} mΩ`, 14, 74);
      doc.text(`U-V: ${formatMilliOhmInput(measuredUV)} mΩ`, 70, 74);
      doc.text(`W-V: ${formatMilliOhmInput(measuredWV)} mΩ`, 126, 74);

      doc.setFillColor(
        mohmOverall ? 22 : 220,
        mohmOverall ? 163 : 38,
        mohmOverall ? 74 : 38
      );
      doc.roundedRect(14, 80, 182, 12, 3, 3, "F");
      doc.setTextColor(255, 255, 255);
      setPdfFont(doc, fontOk, "bold");
      doc.text(
        mohmOverall ? "GENEL SONUÇ: UYGUN" : "GENEL SONUÇ: UYGUN DEĞİL",
        105,
        88,
        { align: "center" }
      );
      doc.setTextColor(0, 0, 0);

      drawPhotoBlock(doc, fontOk, "U-W Ölçüm Fotoğrafı", photos.mohmUW, 14, 102, 58, 44);
      drawPhotoBlock(doc, fontOk, "U-V Ölçüm Fotoğrafı", photos.mohmUV, 76, 102, 58, 44);
      drawPhotoBlock(doc, fontOk, "W-V Ölçüm Fotoğrafı", photos.mohmWV, 138, 102, 58, 44);

      drawReferenceBlock(doc, fontOk, "Sabit Referans U-W", sampleMohmUW, 14, 154, 58, 44);
      drawReferenceBlock(doc, fontOk, "Sabit Referans U-V", sampleMohmUV, 76, 154, 58, 44);
      drawReferenceBlock(doc, fontOk, "Sabit Referans W-V", sampleMohmWV, 138, 154, 58, 44);

      doc.addPage();
      await addPdfHeader(doc, fontOk, "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(16);
      doc.text("Eski ve Yeni Kompresör Terazi KontrolÜ", 14, 30);
      drawPhotoBlock(doc, fontOk, "Eski Kompresör Terazi Fotoğrafı", photos.oldCompWeight, 14, 42, 86, 60);
      drawPhotoBlock(doc, fontOk, "Yeni Kompresör Terazi Fotoğrafı", photos.newCompWeight, 110, 42, 86, 60);
      drawReferenceBlock(doc, fontOk, "Sabit Referans Görsel", sampleWeightControl, 14, 114, 182, 78);

      doc.addPage();
      await addPdfHeader(doc, fontOk, "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(18);
      doc.text("Akış Sonucu ve Uygulama Kararı", 14, 30);

      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(13);
      doc.text(`Sonuç Başlığı: ${flowSummary.finalTitle || "-"}`, 14, 44);

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(11);
      const finalTextLines = doc.splitTextToSize(flowSummary.finalText || "-", 182);
      let y = 54;
      doc.text(finalTextLines, 14, y);
      y += finalTextLines.length * 6 + 6;

      y = await ensurePdfSpace(doc, fontOk, y, (doc.splitTextToSize(flowSummary.path || "-", 182).length * 6 + 8), "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.text("Karar Ağacı:", 14, y);
      y += 8;
      setPdfFont(doc, fontOk, "normal");
      const pathLines = doc.splitTextToSize(flowSummary.path || "-", 182);
      doc.text(pathLines, 14, y);
      y += pathLines.length * 6 + 6;

      y = await ensurePdfSpace(doc, fontOk, y, ((flowSummary.actions || []).length * 6 + 8), "Kompresör Kontrol Formu");
      setPdfFont(doc, fontOk, "bold");
      doc.text("Önerilen İşlemler:", 14, y);
      y += 8;
      setPdfFont(doc, fontOk, "normal");
      for (const action of (flowSummary.actions || [])) {
        const lines = doc.splitTextToSize(`• ${action}`, 178);
        y = await ensurePdfSpace(doc, fontOk, y, (lines.length * 6 + 2), "Kompresör Kontrol Formu");
        doc.text(lines, 18, y);
        y += lines.length * 6 + 2;
      }

      if ((flowSummary.detailRows || []).length > 0) {
        y = await ensurePdfSpace(doc, fontOk, y, ((flowSummary.detailRows || []).length * 6 + 8), "Kompresör Kontrol Formu");
        setPdfFont(doc, fontOk, "bold");
        doc.text("Kontrol Özeti:", 14, y);
        y += 8;
        setPdfFont(doc, fontOk, "normal");
        for (const row of flowSummary.detailRows) {
          const lines = doc.splitTextToSize(`${row.label}: ${row.value}`, 178);
          y = await ensurePdfSpace(doc, fontOk, y, (lines.length * 6 + 2), "Kompresör Kontrol Formu");
          doc.text(lines, 18, y);
          y += lines.length * 6 + 2;
        }
      }

      doc.save("kompresor-kontrol-formu.pdf");
    } catch (err) {
      console.error("PDF HATASI:", err);
      alert("PDF oluşturulurken hata oluştu.");
    }
  };
    const generateAhuPDF = async () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const fontOk = await setupPdfFonts(doc);

      await addPdfHeader(doc, fontOk, "AHU Kontrol");

      const statusOk = ahuCalc.allOk;
      const statusText = statusOk ? "GENEL DURUM: UYGUN" : "GENEL DURUM: KONTROL GEREKİYOR";

      const inputRows = [
        ["Proje", ahuProjectName || "-"],
        ["Servis", ahuServiceName || "-"],
        ["Tarih", ahuDate || "-"],
        ["Dış Ünite Modu", ahuOutdoorMode || "-"],
        ["Kapasite", `${ahuCoolingCapacity || "-"} kW`],
        ["Santral Debisi", ahuAirflow ? `${ahuAirflow} m³/h` : "-"],
        ["DX Devre Sayısı", ahuCircuitCount === "Otomatik" ? `Otomatik (${ahuCalc.circuitCount || "-"})` : ahuCircuitCount || "-"],
        ["Evaporasyon", `${ahuEvapTemp || "-"} °C`],
        ["Superheat", `${ahuSuperheat || "-"} °C`],
        ["Subcool", `${ahuSubcool || "-"} °C`],
        ["Kondenzasyon", `${ahuCondTemp || "-"} °C`],
        ["Batarya Giriş Havası", `${ahuEnteringAirTemp || "-"} °C`],
        ["R410A Basınç Kaybı", `${ahuPressureDrop || "-"} kPa`],
        ["Hava Hızı", `${ahuAirVelocity || "-"} m/s`],
      ];

      const summaryRows = [
        ["Gerekli Toplam HP", ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.requiredTotalHp)} HP` : "-"],
        ["Gerekli Toplam kW", ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.resolvedCapacityKw)} kW` : "-"],
        ["Önerilen Devre", ahuCalc.autoPlan?.circuitCount || "-"],
        ["Kullanılan Devre", ahuCalc.activePlan ? ahuCalc.circuitCount : "-"],
        ["Teorik Debi", ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.theoreticalAirflowM3h)} m³/h` : "-"],
        ["Girilen Debi", ahuCalc.hasAirflow ? `${formatNumber(ahuCalc.resolvedAirflowM3h)} m³/h` : "Girilmedi"],
        ["Seçilen Dış Ünite", ahuCalc.activePlan ? `${formatNumber(ahuCalc.totalSelectedHp)} HP / ${formatNumber(ahuCalc.totalSelectedKw)} kW` : "Seçim bekleniyor"],
        ["Debi Aralığı", ahuCalc.activePlan ? `${formatNumber(ahuCalc.minAirflow)} – ${formatNumber(ahuCalc.maxAirflow)} m³/h` : "-"],
        ["Standart Debi", ahuCalc.activePlan ? `${formatNumber(ahuCalc.selectedStandardAirflowM3h)} m³/h` : "-"],
        ["Toplam EEV", ahuCalc.activePlan ? `${ahuCalc.totalEev} adet` : "-"],
        ["Yuvarlama", ahuCalc.activePlan ? `+${formatNumber(ahuCalc.roundedUpHp)} HP / +${formatNumber(ahuCalc.roundedUpKw)} kW` : "-"],
        ["Seçim Kriteri", ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.label || "-" : "-"],
      ];

      const selectedModelLines = (() => {
        if (!ahuCalc.circuitModelGroups || ahuCalc.circuitModelGroups.length === 0) {
          return ["Cihaz seçimi bekleniyor."];
        }

        const lines = [];
        for (const circuit of ahuCalc.circuitModelGroups) {
          lines.push(`Devre ${circuit.circuitNo}: ${formatNumber(circuit.totalHp)} HP / ${formatNumber(circuit.totalKw)} kW`);
          for (const unit of circuit.units) {
            lines.push(`  ${unit.unitNo}. Ünite: ${unit.hp} HP - ${unit.models.join(" / ") || "-"}`);
          }
        }
        return lines.slice(0, 8);
      })();

      const issueLines = (() => {
        if (ahuCalc.allOk) return ["Tüm zorunlu alanlar ve ana teknik kriterler uygundur."];
        const failedRows = (ahuCalc.validationRows || []).filter((row) => row.ok === false);
        const items = [];

        if (ahuCalc.systemReason) {
          items.push(ahuCalc.systemReason);
        }

        for (const row of failedRows) {
          const valueText = row.value && row.value !== "-" ? ` (${row.value})` : "";
          items.push(`${row.label}${valueText} — Kural: ${row.rule}`);
        }

        return Array.from(new Set(items)).slice(0, 7);
      })();

      const controlRows = (ahuCalc.validationRows || []).map((row) => ({
        label: row.label,
        value: row.value,
        rule: row.rule,
        ok: row.ok,
      }));

      const drawSectionTitle = (title, x, y) => {
        setPdfFont(doc, fontOk, "bold");
        doc.setFontSize(10.2);
        doc.setTextColor(15, 23, 42);
        doc.text(title, x, y);
        doc.setTextColor(0, 0, 0);
      };

      const drawPairRows = (rows, x, y, w, h, opts = {}) => {
        const {
          fill = [248, 250, 252],
          line = [226, 232, 240],
          title = "",
          titleColor = [15, 23, 42],
          columns = 1,
          fontSize = 7.2,
        } = opts;

        doc.setFillColor(...fill);
        doc.setDrawColor(...line);
        doc.roundedRect(x, y, w, h, 4, 4, "FD");

        let currentY = y + 8;
        if (title) {
          setPdfFont(doc, fontOk, "bold");
          doc.setFontSize(10.2);
          doc.setTextColor(...titleColor);
          doc.text(title, x + 4, currentY);
          currentY += 7;
        }

        const colGap = 3;
        const colW = (w - 8 - colGap * (columns - 1)) / columns;
        const rowH = 7.3;

        rows.forEach((row, index) => {
          const col = columns === 1 ? 0 : index % columns;
          const rowIndex = columns === 1 ? index : Math.floor(index / columns);
          const cellX = x + 4 + col * (colW + colGap);
          const cellY = currentY + rowIndex * rowH;
          if (cellY > y + h - 5) return;

          const label = Array.isArray(row) ? row[0] : row.label;
          const value = Array.isArray(row) ? row[1] : row.value;

          setPdfFont(doc, fontOk, "bold");
          doc.setFontSize(fontSize - 0.5);
          doc.setTextColor(100, 116, 139);
          doc.text(String(label || "-"), cellX, cellY);

          setPdfFont(doc, fontOk, "bold");
          doc.setFontSize(fontSize + 0.7);
          doc.setTextColor(15, 23, 42);
          const wrapped = doc.splitTextToSize(String(value || "-"), colW);
          doc.text(wrapped.slice(0, 1), cellX, cellY + 3.8);
        });

        doc.setTextColor(0, 0, 0);
      };

      const drawStatusCard = (x, y, w, h) => {
        doc.setFillColor(statusOk ? 22 : 220, statusOk ? 163 : 38, statusOk ? 74 : 38);
        doc.setDrawColor(statusOk ? 22 : 220, statusOk ? 163 : 38, statusOk ? 74 : 38);
        doc.roundedRect(x, y, w, h, 5, 5, "FD");
        setPdfFont(doc, fontOk, "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(255, 255, 255);
        doc.text("GENEL DURUM", x + 5, y + 7);
        doc.setFontSize(13);
        doc.text(statusText.replace("GENEL DURUM: ", ""), x + 5, y + 14);
        doc.setTextColor(0, 0, 0);
      };

      const drawIssueBox = (x, y, w, h) => {
        doc.setFillColor(statusOk ? 240 : 254, statusOk ? 253 : 226, statusOk ? 244 : 226);
        doc.setDrawColor(statusOk ? 134 : 252, statusOk ? 239 : 165, statusOk ? 172 : 165);
        doc.roundedRect(x, y, w, h, 4, 4, "FD");
        setPdfFont(doc, fontOk, "bold");
        doc.setFontSize(8.4);
        doc.setTextColor(statusOk ? 22 : 153, statusOk ? 101 : 27, statusOk ? 52 : 27);
        doc.text(statusOk ? "Kontrol Notu" : "Kontrol Gereken Maddeler", x + 4, y + 7);

        setPdfFont(doc, fontOk, "normal");
        doc.setFontSize(6.8);
        let yy = y + 13;
        for (const item of issueLines) {
          const wrapped = doc.splitTextToSize(`• ${item}`, w - 8);
          for (const line of wrapped.slice(0, 2)) {
            if (yy > y + h - 4) break;
            doc.text(line, x + 4, yy);
            yy += 3.5;
          }
          if (yy > y + h - 4) break;
        }
        doc.setTextColor(0, 0, 0);
      };

      const drawModelBox = (x, y, w, h) => {
        doc.setFillColor(248, 250, 252);
        doc.setDrawColor(226, 232, 240);
        doc.roundedRect(x, y, w, h, 4, 4, "FD");
        drawSectionTitle("Seçilen Cihaz Modelleri", x + 4, y + 8);
        setPdfFont(doc, fontOk, "normal");
        doc.setFontSize(7.0);
        let yy = y + 16;
        for (const line of selectedModelLines) {
          const wrapped = doc.splitTextToSize(line, w - 8);
          for (const part of wrapped.slice(0, 1)) {
            if (yy > y + h - 4) break;
            doc.text(part, x + 4, yy);
            yy += 4;
          }
          if (yy > y + h - 4) break;
        }
      };

      const drawControlGrid = (x, y, w, h) => {
        drawSectionTitle("Kontrol Sonuçları", x, y);
        const cols = 4;
        const gap = 3;
        const cardW = (w - gap * (cols - 1)) / cols;
        const cardH = 13;
        const startY = y + 6;
        const maxCards = Math.min(controlRows.length, 16);

        for (let i = 0; i < maxCards; i += 1) {
          const row = controlRows[i];
          const col = i % cols;
          const r = Math.floor(i / cols);
          const cx = x + col * (cardW + gap);
          const cy = startY + r * (cardH + 3);

          const isOk = row.ok === true;
          const isBad = row.ok === false;
          doc.setFillColor(isOk ? 220 : isBad ? 254 : 248, isOk ? 252 : isBad ? 226 : 250, isOk ? 231 : isBad ? 226 : 252);
          doc.setDrawColor(isOk ? 134 : isBad ? 252 : 226, isOk ? 239 : isBad ? 165 : 232, isOk ? 172 : isBad ? 165 : 240);
          doc.roundedRect(cx, cy, cardW, cardH, 3, 3, "FD");

          setPdfFont(doc, fontOk, "bold");
          doc.setFontSize(5.6);
          doc.setTextColor(isOk ? 22 : isBad ? 153 : 71, isOk ? 101 : isBad ? 27 : 85, isOk ? 52 : isBad ? 27 : 105);
          const label = doc.splitTextToSize(String(row.label || "-"), cardW - 4);
          doc.text(label.slice(0, 1), cx + 2, cy + 4);

          setPdfFont(doc, fontOk, "bold");
          doc.setFontSize(6.5);
          doc.setTextColor(15, 23, 42);
          const value = doc.splitTextToSize(String(row.value || "-"), cardW - 4);
          doc.text(value.slice(0, 1), cx + 2, cy + 8.5);

          setPdfFont(doc, fontOk, "normal");
          doc.setFontSize(5.0);
          doc.setTextColor(100, 116, 139);
          const rule = doc.splitTextToSize(String(row.rule || "-"), cardW - 4);
          doc.text(rule.slice(0, 1), cx + 2, cy + 11.5);
        }
        doc.setTextColor(0, 0, 0);
      };

      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(16);
      doc.text("AHU Kontrol Ekran Özeti", 14, 30);
      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text("Ekrandaki girişler, hesap özeti, seçilen cihazlar ve kontrol sonuçları tek sayfada özetlenmiştir.", 14, 36);
      doc.setTextColor(0, 0, 0);

      drawPairRows(inputRows, 14, 43, 87, 106, {
        title: "Giriş Parametreleri",
        columns: 1,
        fontSize: 6.7,
      });

      drawStatusCard(107, 43, 89, 18);
      drawIssueBox(107, 66, 89, 28);

      drawPairRows(summaryRows, 107, 99, 89, 50, {
        title: "Hesap Özeti",
        columns: 2,
        fontSize: 5.8,
        fill: [248, 250, 252],
        line: [226, 232, 240],
      });

      drawModelBox(14, 157, 182, 37);

      drawControlGrid(14, 205, 182, 70);

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100, 116, 139);
      doc.text("Not: PDF tuşu bu çıktıya dahil edilmemiştir. Detaylı hesaplar uygulama ekranında kontrol edilebilir.", 14, 286);
      doc.setTextColor(0, 0, 0);

      // 2. sayfa: Kısa AHU Kontrol Özeti
      doc.addPage();
      await addPdfHeader(doc, fontOk, "AHU Kontrol");

      const ahuCompactLoadText = ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.label || "-" : "-";
      const ahuCompactSelectedDeviceText = ahuCalc.activePlan
        ? `${formatNumber(ahuCalc.totalSelectedHp)} HP / ${formatNumber(ahuCalc.totalSelectedKw)} kW`
        : "Seçim bekleniyor";
      const ahuCompactAirflowText = ahuCalc.activePlan
        ? `${formatNumber(ahuCalc.minAirflow)} – ${formatNumber(ahuCalc.maxAirflow)} m³/h`
        : "-";

      const ahuCompactInputLines = [
        `Proje: ${ahuProjectName || "-"} | Servis: ${ahuServiceName || "-"} | Tarih: ${ahuDate || "-"}`,
        `Mod: ${ahuOutdoorMode} | Kapasite: ${ahuCoolingCapacity || "-"} kW | Debi: ${ahuAirflow || "-"} m³/h`,
        `DX Devre: ${ahuCircuitCount === "Otomatik" ? `Otomatik (${ahuCalc.circuitCount || "-"})` : ahuCircuitCount || "-"}`,
        `Evap: ${ahuEvapTemp || "-"} °C | SH: ${ahuSuperheat || "-"} °C | SC: ${ahuSubcool || "-"} °C | Kond.: ${ahuCondTemp || "-"} °C`,
        `Giriş Hava: ${ahuEnteringAirTemp || "-"} °C | R410A ΔP: ${ahuPressureDrop || "-"} kPa | Hava Hızı: ${ahuAirVelocity || "-"} m/s`,
      ];

      const ahuCompactExpectedLines = [
        `Seçim Kriteri: ${ahuCompactLoadText}`,
        `İhtiyaç: ${ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.requiredTotalHp)} HP / ${formatNumber(ahuCalc.resolvedCapacityKw)} kW` : "-"}`,
        `Seçilen Dış Ünite: ${ahuCompactSelectedDeviceText}`,
        `Devre Sayısı: ${ahuCalc.activePlan ? ahuCalc.circuitCount : "-"} | EEV: ${ahuCalc.activePlan ? `${ahuCalc.totalEev} adet` : "-"}`,
        `Debi Aralığı: ${ahuCompactAirflowText}`,
        `Teorik Debi: ${ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.theoreticalAirflowM3h)} m³/h` : "-"}`,
        `Girilen Debi: ${ahuCalc.hasAirflow ? `${formatNumber(ahuCalc.resolvedAirflowM3h)} m³/h` : "Girilmedi"}`,
      ];

      const ahuCompactIssues = (() => {
        if (ahuCalc.allOk) return ["Tüm zorunlu alanlar ve ana teknik kriterler uygundur."];
        const items = [];
        if (!ahuCalc.hasLoadInput) items.push("Kapasite veya debi bilgisi girilmelidir.");
        if (!ahuCalc.activePlan && ahuCalc.hasLoadInput) items.push(ahuCalc.systemReason || "Uygun dış ünite seçimi bulunamadı.");
        for (const row of (ahuCalc.validationRows || []).filter((item) => item.ok === false)) {
          const valueText = row.value && row.value !== "-" ? ` (${row.value})` : "";
          items.push(`${row.label}${valueText} - Kural: ${row.rule}`);
        }
        if (items.length === 0) items.push(ahuCalc.systemReason || "Kontrol edilmesi gereken değerler var.");
        return Array.from(new Set(items)).slice(0, 7);
      })();

      const ahuCompactDrawTextLines = (lines, x, y, maxWidth, maxY, fontSize = 8.4) => {
        let currentY = y;
        doc.setFontSize(fontSize);
        for (const lineItem of lines) {
          if (currentY > maxY) break;
          setPdfFont(doc, fontOk, "normal");
          const wrapped = doc.splitTextToSize(String(lineItem || "-"), maxWidth);
          for (const line of wrapped) {
            if (currentY > maxY) {
              doc.text("...", x, currentY);
              return;
            }
            doc.text(line, x, currentY);
            currentY += fontSize * 0.42 + 1.05;
          }
        }
      };

      const ahuCompactDrawBox = ({ title, lines, x, y, w, h, fill, line, titleColor = [15, 23, 42], fontSize = 8.4 }) => {
        doc.setFillColor(...fill);
        doc.setDrawColor(...line);
        doc.roundedRect(x, y, w, h, 4, 4, "FD");
        setPdfFont(doc, fontOk, "bold");
        doc.setFontSize(11.3);
        doc.setTextColor(...titleColor);
        doc.text(title, x + 4, y + 8);
        doc.setTextColor(15, 23, 42);
        ahuCompactDrawTextLines(lines, x + 4, y + 17, w - 8, y + h - 6, fontSize);
        doc.setTextColor(0, 0, 0);
      };

      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(17);
      doc.text("AHU Kontrol Özeti", 14, 31);
      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text("Girilen değerler, hesaplanan seçim ve kontrol sonucu tek sayfada özetlenmiştir.", 14, 38);
      doc.setTextColor(0, 0, 0);

      doc.setFillColor(ahuCalc.allOk ? 220 : 254, ahuCalc.allOk ? 252 : 226, ahuCalc.allOk ? 231 : 226);
      doc.setDrawColor(ahuCalc.allOk ? 134 : 252, ahuCalc.allOk ? 239 : 165, ahuCalc.allOk ? 172 : 165);
      doc.roundedRect(14, 44, 182, 18, 4, 4, "FD");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(14);
      doc.setTextColor(ahuCalc.allOk ? 22 : 153, ahuCalc.allOk ? 101 : 27, ahuCalc.allOk ? 52 : 27);
      doc.text(ahuCalc.allOk ? "GENEL DURUM: UYGUN" : "GENEL DURUM: KONTROL GEREKİYOR", 105, 56, { align: "center" });
      doc.setTextColor(0, 0, 0);

      ahuCompactDrawBox({
        title: "Girilen Değerler",
        lines: ahuCompactInputLines,
        x: 14,
        y: 70,
        w: 182,
        h: 47,
        fill: [248, 250, 252],
        line: [226, 232, 240],
      });

      ahuCompactDrawBox({
        title: "Hesaplanan Seçim",
        lines: ahuCompactExpectedLines,
        x: 14,
        y: 125,
        w: 88,
        h: 70,
        fill: [240, 253, 244],
        line: [134, 239, 172],
        titleColor: [22, 101, 52],
      });

      ahuCompactDrawBox({
        title: "Seçilen Cihaz / Devre",
        lines: selectedModelLines,
        x: 108,
        y: 125,
        w: 88,
        h: 70,
        fill: [239, 246, 255],
        line: [147, 197, 253],
        titleColor: [30, 64, 175],
        fontSize: 8.0,
      });

      ahuCompactDrawBox({
        title: ahuCalc.allOk ? "Kontrol Notu" : "Kontrol Gereken Maddeler",
        lines: ahuCompactIssues.map((item) => `• ${item}`),
        x: 14,
        y: 203,
        w: 182,
        h: 52,
        fill: ahuCalc.allOk ? [240, 253, 244] : [255, 247, 237],
        line: ahuCalc.allOk ? [134, 239, 172] : [253, 186, 116],
        titleColor: ahuCalc.allOk ? [22, 101, 52] : [154, 52, 18],
        fontSize: 8.2,
      });

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text("Not: 1. sayfa ekran görünümü, 2. sayfa kısa teknik özet olarak hazırlanmıştır.", 14, 274);
      doc.setTextColor(0, 0, 0);


      doc.save("ahu-kontrol-ekran-ozeti.pdf");
    } catch (err) {
      console.error("AHU PDF HATASI:", err);
      alert("AHU PDF oluşturulurken hata oluştu.");
    }
  };


  const buildAhuFieldReportData = () => {
    const issues = ahuFieldComparison.issues.length > 0
      ? ahuFieldComparison.issues
      : [ahuFieldComparison.summary || "Saha karşılaştırması için eksik bilgi var."];

    const expectedRows = [
      ["Bağlantı Tipi", ahuFieldComparison.connectionTypeLabel || "-"],
      ["Seçim Kriteri", ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.label || "-" : "-"],
      ["Seçim Açıklaması", ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.reason || "-" : "Kapasite veya debi girilmedi."],
      ["Gerekli Toplam HP", ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.requiredTotalHp)} HP` : "-"],
      ["Gerekli Toplam kW", ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.resolvedCapacityKw)} kW` : "-"],
      ["Olması Gereken Dış Ünite", ahuFieldComparison.hasExpectedSelection ? `${formatNumber(ahuFieldComparison.expectedHp)} HP / ${formatNumber(ahuFieldComparison.expectedKw)} kW` : "Seçim bekleniyor"],
      ["Devre Sayısı", ahuFieldComparison.hasExpectedSelection ? `${ahuFieldComparison.expectedCircuitCount}` : "-"],
      ["Toplam EEV", ahuFieldComparison.hasExpectedSelection ? `${ahuFieldComparison.expectedEev} adet` : "-"],
      ["Debi Aralığı", ahuFieldComparison.hasExpectedSelection ? `${formatNumber(ahuFieldComparison.expectedMinAirflow)} – ${formatNumber(ahuFieldComparison.expectedMaxAirflow)} m³/h` : "-"],
    ];

    const expectedCircuitRows = [];
    if (ahuFieldComparison.expectedCircuitGroups && ahuFieldComparison.expectedCircuitGroups.length > 0) {
      for (const circuit of ahuFieldComparison.expectedCircuitGroups) {
        expectedCircuitRows.push({
          title: `Devre ${circuit.circuitNo}`,
          subtitle: `${formatNumber(circuit.totalHp)} HP / ${formatNumber(circuit.totalKw)} kW`,
          lines: circuit.units.map((unit) => `${unit.unitNo}. Ünite — ${unit.hp} HP — ${unit.models.join(" / ") || "-"}`),
        });
      }
    }

    const appliedCircuitBlocks = ahuFieldComparison.appliedDisplayBlocks || [];

    const comparisonRows = ahuFieldComparison.rows.map((row) => ({
      label: row.label,
      status: row.ok ? "UYGUN" : "UYGUN DEĞİL",
      expected: row.expected,
      applied: row.applied,
      reason: row.reason,
      ok: row.ok,
    }));

    const singleCircuitMultiOutdoorNotes = [
      ...(ahuFieldComparison.hasSharedMultiCircuit ? [FIELD_SHARED_CONNECTION_WARNING] : []),
      ...ahuFieldComparison.appliedCircuits
        .filter((circuit) => !circuit.isShared && circuit.units.length > 1)
        .map((circuit) => {
          const isEco = ahuFieldOutdoorMode === "Eco";
          return isEco
            ? `Devre ${circuit.circuitNo}: Eco modda aynı devreye birden fazla dış ünite seçilmemelidir.`
            : `Devre ${circuit.circuitNo}: Standart modda aynı devreye birden fazla dış ünite girilebilir; ancak devre bazlı HP dağılımı olması gereken hesapla aynı olmalıdır.`;
        }),
    ];

    return {
      title: "Saha Uygulaması Karşılaştırması",
      subtitle: "Olması gereken hesap ile sahada uygulanan dış ünite seçimi karşılaştırması",
      ok: ahuFieldComparison.allOk,
      issues,
      expectedRows,
      expectedCircuitRows,
      appliedCircuitBlocks,
      comparisonRows,
      singleCircuitMultiOutdoorNotes,
    };
  };

  const generateAhuFieldPDF = async () => {
    try {
      const report = buildAhuFieldReportData();
      const doc = new jsPDF("p", "mm", "a4");
      const fontOk = await setupPdfFonts(doc);

      await addPdfHeader(doc, fontOk, "Saha Uygulaması");

      const getValue = (rows, label) => {
        const row = (rows || []).find(([key]) => key === label);
        return row ? row[1] : "-";
      };

      const compactIssues = (() => {
        const preferred = [
          "Bağlantı Tipi",
          "Saha Dış Ünitesine Göre Debi Kontrolü",
          "Toplam Dış Ünite HP",
          "Toplam EEV",
          "DX Devre Sayısı",
          "Dış Ünite Modu",
          "Ana Teknik Kontrol",
        ];
        const failed = (report.comparisonRows || []).filter((row) => row.ok !== true);
        const ordered = [
          ...preferred.flatMap((label) => failed.filter((row) => row.label === label)),
          ...failed.filter((row) => !preferred.includes(row.label)),
        ];
        const seen = new Set();
        const items = [];
        for (const row of ordered) {
          let text = row.reason || row.label;
          if (row.label === "Bağlantı Tipi") {
            text = "Tek gövde dış ünitenin birden fazla bağımsız DX devreyi ortak beslemesi uygun değildir.";
          }
          const key = text.replace(/\s+/g, " ").trim();
          if (seen.has(key)) continue;
          seen.add(key);
          items.push(text);
          if (items.length >= 5) break;
        }
        return report.ok ? ["Kritik uygunsuzluk bulunmadı."] : items;
      })();

      const expectedCircuitShort = (report.expectedCircuitRows || [])
        .map((block) => `${block.title}: ${block.subtitle}`)
        .join(" / ") || "Seçim bekleniyor";

      const appliedModelLines = (report.appliedCircuitBlocks || []).flatMap((block) => [
        `${block.title}: ${block.subtitle}`,
        ...((block.lines || []).slice(0, 2)),
      ]);

      const inputLines = [
        `Proje: ${ahuProjectName || "-"} | Servis: ${ahuServiceName || "-"} | Tarih: ${ahuDate || "-"}`,
        `Mod: ${ahuOutdoorMode} | Kapasite: ${ahuCoolingCapacity || "-"} kW | Debi: ${ahuAirflow || "-"} m³/h | DX Devre: ${ahuCircuitCount === "Otomatik" ? `Otomatik (${ahuCalc.circuitCount})` : ahuCircuitCount}`,
        `Evap: ${ahuEvapTemp || "-"} °C | SH: ${ahuSuperheat || "-"} °C | SC: ${ahuSubcool || "-"} °C | Kond.: ${ahuCondTemp || "-"} °C`,
        `Giriş Hava: ${ahuEnteringAirTemp || "-"} °C | R410A ΔP: ${ahuPressureDrop || "-"} kPa | Hava Hızı: ${ahuAirVelocity || "-"} m/s`,
        `Bağlantı Tipi: ${ahuFieldComparison.connectionTypeLabel || "-"}`,
      ];

      const expectedLines = [
        `Seçim: ${getValue(report.expectedRows, "Seçim Kriteri")}`,
        `İhtiyaç: ${getValue(report.expectedRows, "Gerekli Toplam HP")} / ${getValue(report.expectedRows, "Gerekli Toplam kW")}`,
        `Dış Ünite: ${getValue(report.expectedRows, "Olması Gereken Dış Ünite")}`,
        `Devre: ${getValue(report.expectedRows, "Devre Sayısı")} / EEV: ${getValue(report.expectedRows, "Toplam EEV")}`,
        `Debi Aralığı: ${getValue(report.expectedRows, "Debi Aralığı")}`,
        `Dağılım: ${expectedCircuitShort}`,
      ];

      const appliedLines = [
        `Bağlantı: ${ahuFieldComparison.connectionTypeLabel || "-"}`,
        `Toplam: ${formatNumber(ahuFieldComparison.appliedTotalHp || 0)} HP / ${formatNumber(ahuFieldComparison.appliedTotalKw || 0)} kW`,
        `Devre: ${ahuFieldComparison.appliedCircuitCount || 0} / EEV: ${ahuFieldComparison.appliedTotalEev || 0} adet`,
        `Debi Aralığı: ${formatNumber(ahuFieldComparison.appliedMinAirflow || 0)} – ${formatNumber(ahuFieldComparison.appliedMaxAirflow || 0)} m³/h`,
        ...(appliedModelLines.length > 0 ? appliedModelLines : ["Sahada uygulanan dış ünite seçimi yapılmadı."]),
      ];

      const drawTextLines = (lines, x, y, maxWidth, maxY, fontSize = 8.7, boldFirst = false) => {
        let currentY = y;
        doc.setFontSize(fontSize);
        for (let i = 0; i < lines.length; i += 1) {
          if (currentY > maxY) break;
          setPdfFont(doc, fontOk, boldFirst && i === 0 ? "bold" : "normal");
          const wrapped = doc.splitTextToSize(String(lines[i] || "-"), maxWidth);
          for (const line of wrapped) {
            if (currentY > maxY) {
              doc.text("...", x, currentY);
              return;
            }
            doc.text(line, x, currentY);
            currentY += fontSize * 0.42 + 1.1;
          }
        }
      };

      const drawBox = ({ title, lines, x, y, w, h, fill, line, titleColor = [15, 23, 42] }) => {
        doc.setFillColor(...fill);
        doc.setDrawColor(...line);
        doc.roundedRect(x, y, w, h, 4, 4, "FD");
        setPdfFont(doc, fontOk, "bold");
        doc.setFontSize(11.5);
        doc.setTextColor(...titleColor);
        doc.text(title, x + 4, y + 8);
        doc.setTextColor(15, 23, 42);
        drawTextLines(lines, x + 4, y + 17, w - 8, y + h - 6, 8.5);
        doc.setTextColor(0, 0, 0);
      };

      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(17);
      doc.text("Saha Uygulaması Karşılaştırma Özeti", 14, 31);
      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text("Giriş değerleri, olması gereken seçim ve sahada uygulanan seçim tek sayfada özetlenmiştir.", 14, 38);
      doc.setTextColor(0, 0, 0);

      doc.setFillColor(report.ok ? 220 : 254, report.ok ? 252 : 226, report.ok ? 231 : 226);
      doc.setDrawColor(report.ok ? 134 : 252, report.ok ? 239 : 165, report.ok ? 172 : 165);
      doc.roundedRect(14, 44, 182, 18, 4, 4, "FD");
      setPdfFont(doc, fontOk, "bold");
      doc.setFontSize(14);
      doc.setTextColor(report.ok ? 22 : 153, report.ok ? 101 : 27, report.ok ? 52 : 27);
      doc.text(report.ok ? "SONUÇ: UYGUN" : "SONUÇ: UYGUN DEĞİL / KONTROL GEREKİYOR", 105, 56, { align: "center" });
      doc.setTextColor(0, 0, 0);

      drawBox({
        title: "Girilen Değerler",
        lines: inputLines,
        x: 14,
        y: 70,
        w: 182,
        h: 45,
        fill: [248, 250, 252],
        line: [226, 232, 240],
      });

      drawBox({
        title: "Olması Gereken",
        lines: expectedLines,
        x: 14,
        y: 123,
        w: 88,
        h: 78,
        fill: [240, 253, 244],
        line: [134, 239, 172],
        titleColor: [22, 101, 52],
      });

      drawBox({
        title: "Sahada Uygulanan",
        lines: appliedLines,
        x: 108,
        y: 123,
        w: 88,
        h: 78,
        fill: report.ok ? [240, 253, 244] : [254, 242, 242],
        line: report.ok ? [134, 239, 172] : [252, 165, 165],
        titleColor: report.ok ? [22, 101, 52] : [153, 27, 27],
      });

      drawBox({
        title: report.ok ? "Kontrol Notu" : "Kontrol Gereken Maddeler",
        lines: compactIssues.map((item) => `• ${item}`),
        x: 14,
        y: 210,
        w: 182,
        h: 50,
        fill: report.ok ? [240, 253, 244] : [255, 247, 237],
        line: report.ok ? [134, 239, 172] : [253, 186, 116],
        titleColor: report.ok ? [22, 101, 52] : [154, 52, 18],
      });

      setPdfFont(doc, fontOk, "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text("Not: Ayrıntılı devre / model bilgileri saha karşılaştırma ekranında görülebilir. PDF/PPT çıktısı kısa yönetici özeti olarak hazırlanmıştır.", 14, 274);
      doc.setTextColor(0, 0, 0);

      doc.save("saha-uygulamasi-karsilastirma-ozet.pdf");
    } catch (err) {
      console.error("SAHA KARŞILAŞTIRMA PDF HATASI:", err);
      alert("Saha karşılaştırma PDF'i oluşturulurken hata oluştu.");
    }
  };

  const generateAhuFieldPPT = async () => {
    try {
      const report = buildAhuFieldReportData();
      const pptx = new pptxgen();
      pptx.layout = "LAYOUT_WIDE";
      pptx.author = "Samsung Klima Sistemleri";
      pptx.subject = "Saha Uygulaması Karşılaştırması";
      pptx.title = "Saha Uygulaması Karşılaştırması";
      pptx.company = "Samsung";
      pptx.lang = "tr-TR";
      pptx.theme = {
        headFontFace: "Arial",
        bodyFontFace: "Arial",
        lang: "tr-TR",
      };

      const getValue = (rows, label) => {
        const row = (rows || []).find(([key]) => key === label);
        return row ? row[1] : "-";
      };

      const issueItems = (() => {
        const preferred = ["Bağlantı Tipi", "Devre Başı HP Sınırı", "DX Devre Sayısı", "Devre Bazlı HP Dağılımı", "Saha Dış Ünitesine Göre Debi Kontrolü", "Toplam Dış Ünite HP", "Toplam EEV", "Ana Teknik Kontrol"];
        const failed = (report.comparisonRows || []).filter((row) => row.ok !== true);
        const ordered = [...preferred.flatMap((label) => failed.filter((row) => row.label === label)), ...failed.filter((row) => !preferred.includes(row.label))];
        const seen = new Set();
        const items = [];
        for (const row of ordered) {
          let text = row.reason || row.label;
          if (row.label === "Bağlantı Tipi") text = "Tek gövde dış ünite birden fazla bağımsız DX devreyi ortak beslememelidir.";
          const key = text.replace(/\s+/g, " ").trim();
          if (seen.has(key)) continue;
          seen.add(key);
          items.push(text);
          if (items.length >= 4) break;
        }
        return report.ok ? ["Kritik uygunsuzluk bulunmadı."] : items;
      })();

      const appliedModelLines = (report.appliedCircuitBlocks || []).flatMap((block) => [
        `${block.title}: ${block.subtitle}`,
        ...((block.lines || []).slice(0, 4)),
      ]);
      const expectedCircuitShort = (report.expectedCircuitRows || []).map((block) => `${block.title}: ${block.subtitle}`).join(" / ") || "Seçim bekleniyor";

      const inputText = [
        `Proje: ${ahuProjectName || "-"} | Servis: ${ahuServiceName || "-"} | Tarih: ${ahuDate || "-"}`,
        `Mod: ${ahuOutdoorMode} | Kapasite: ${ahuCoolingCapacity || "-"} kW | Debi: ${ahuAirflow || "-"} m³/h | DX: ${ahuCircuitCount === "Otomatik" ? `Otomatik (${ahuCalc.circuitCount})` : ahuCircuitCount}`,
        `Evap: ${ahuEvapTemp || "-"} °C | SH: ${ahuSuperheat || "-"} °C | SC: ${ahuSubcool || "-"} °C | Kond.: ${ahuCondTemp || "-"} °C`,
        `Giriş Hava: ${ahuEnteringAirTemp || "-"} °C | R410A ΔP: ${ahuPressureDrop || "-"} kPa | Hız: ${ahuAirVelocity || "-"} m/s`,
        `Bağlantı: ${ahuFieldComparison.connectionTypeLabel || "-"}`,
      ].join("\n");

      const expectedText = [
        `Seçim: ${getValue(report.expectedRows, "Seçim Kriteri")}`,
        `İhtiyaç: ${getValue(report.expectedRows, "Gerekli Toplam HP")} / ${getValue(report.expectedRows, "Gerekli Toplam kW")}`,
        `Dış Ünite: ${getValue(report.expectedRows, "Olması Gereken Dış Ünite")}`,
        `Devre / EEV: ${getValue(report.expectedRows, "Devre Sayısı")} / ${getValue(report.expectedRows, "Toplam EEV")}`,
        `Debi: ${getValue(report.expectedRows, "Debi Aralığı")}`,
        `Dağılım: ${expectedCircuitShort}`,
      ].join("\n");

      const appliedText = [
        `Toplam: ${formatNumber(ahuFieldComparison.appliedTotalHp || 0)} HP / ${formatNumber(ahuFieldComparison.appliedTotalKw || 0)} kW`,
        `Devre / EEV: ${ahuFieldComparison.appliedCircuitCount || 0} / ${ahuFieldComparison.appliedTotalEev || 0}`,
        `Debi: ${formatNumber(ahuFieldComparison.appliedMinAirflow || 0)} – ${formatNumber(ahuFieldComparison.appliedMaxAirflow || 0)} m³/h`,
        ...(appliedModelLines.length > 0 ? appliedModelLines : ["Sahada uygulanan seçim yok."]),
      ].join("\n");

      const slide = pptx.addSlide();
      slide.background = { color: "F8FAFC" };
      slide.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: 13.333, h: 0.42, fill: { color: "111827" }, line: { color: "111827" } });
      slide.addText("Saha Uygulaması Karşılaştırma Özeti", { x: 0.55, y: 0.65, w: 12.2, h: 0.4, fontSize: 22, bold: true, color: "111827" });
      slide.addShape(pptx.ShapeType.roundRect, { x: 0.55, y: 1.15, w: 12.25, h: 0.7, rectRadius: 0.12, fill: { color: report.ok ? "DCFCE7" : "FEE2E2" }, line: { color: report.ok ? "86EFAC" : "FCA5A5" } });
      slide.addText(report.ok ? "SONUÇ: UYGUN" : "SONUÇ: UYGUN DEĞİL / KONTROL GEREKİYOR", { x: 0.75, y: 1.35, w: 11.9, h: 0.25, fontSize: 17, bold: true, color: report.ok ? "166534" : "991B1B", align: "center" });

      const addCard = (title, text, x, y, w, h, fill, line, titleColor, textFontSize = 8.4) => {
        slide.addShape(pptx.ShapeType.roundRect, { x, y, w, h, rectRadius: 0.12, fill: { color: fill }, line: { color: line } });
        slide.addText(title, { x: x + 0.18, y: y + 0.14, w: w - 0.36, h: 0.25, fontSize: 12.5, bold: true, color: titleColor });
        slide.addText(text || "-", { x: x + 0.18, y: y + 0.48, w: w - 0.36, h: h - 0.58, fontSize: textFontSize, color: "111827", fit: "shrink", valign: "top", breakLine: false });
      };

      addCard("Girilen Değerler", inputText, 0.55, 2.05, 12.25, 1.2, "FFFFFF", "CBD5E1", "111827", 8.1);
      addCard("Olması Gereken", expectedText, 0.55, 3.38, 5.95, 2.42, "F0FDF4", "86EFAC", "166534", 8.05);
      addCard("Sahada Uygulanan", appliedText, 6.85, 3.38, 5.95, 2.42, report.ok ? "F0FDF4" : "FEF2F2", report.ok ? "86EFAC" : "FCA5A5", report.ok ? "166534" : "991B1B", 8.05);
      addCard(report.ok ? "Kontrol Notu" : "Kontrol Gereken Maddeler", issueItems.map((item) => `• ${item}`).join("\n"), 0.55, 6.05, 12.25, 1.05, report.ok ? "F0FDF4" : "FFF7ED", report.ok ? "86EFAC" : "FDBA74", report.ok ? "166534" : "9A3412", 7.7);

      await pptx.writeFile({ fileName: "saha-uygulamasi-karsilastirma-ozet.pptx" });
    } catch (err) {
      console.error("SAHA KARŞILAŞTIRMA PPT HATASI:", err);
      alert("PPT çıktısı oluşturulurken hata oluştu. Eğer build hatası alırsan pptxgenjs paketini yükle: npm install pptxgenjs@3.12.0");
    }
  };

  const renderResultCard = (label, ok) => {
    const isOk = ok === true;
    const isFail = ok === false;

    return (
      <div
        style={{
          flex: 1,
          minWidth: 90,
          borderRadius: 14,
          padding: "12px 10px",
          background: isOk ? "#dcfce7" : isFail ? "#fee2e2" : "#f8fafc",
          border: `1px solid ${isOk ? "#86efac" : isFail ? "#fecaca" : "#e5e7eb"}`,
          color: isOk ? "#166534" : isFail ? "#991b1b" : "#334155",
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 13 }}>{label}</div>
        <div style={{ marginTop: 6, fontSize: 14 }}>
          {isOk ? "Uygun" : isFail ? "Uygun Değil" : "Bekleniyor"}
        </div>
      </div>
    );
  };

  const renderAhuValidationCard = (row) => {
    const getBg = () => {
      if (row.ok === true) return "#dcfce7";
      if (row.ok === false) return "#fee2e2";
      return "#f8fafc";
    };

    const getBorder = () => {
      if (row.ok === true) return "#86efac";
      if (row.ok === false) return "#fecaca";
      return "#e5e7eb";
    };

    const getColor = () => {
      if (row.ok === true) return "#166534";
      if (row.ok === false) return "#991b1b";
      return "#334155";
    };

    return (
      <div
        key={row.label}
        style={{
          borderRadius: 12,
          background: getBg(),
          border: `1px solid ${getBorder()}`,
          padding: "12px 14px",
        }}
      >
        <div style={{ fontWeight: 800, color: "#111827", marginBottom: 4 }}>
          {row.label}
        </div>
        <div style={{ color: getColor(), fontWeight: 700, marginBottom: 4 }}>
          {row.value}
        </div>
        <div style={{ color: "#64748b", fontSize: 13 }}>Kural: {row.rule}</div>
      </div>
    );
  };

  const ReferenceImage = ({ src, title, height = 220 }) => (
    <div style={referenceWrapStyle}>
      <div style={referenceTitleStyle}>{title}</div>
      <div style={referenceImageBoxStyle}>
        <img
          src={src}
          alt={title}
          style={{
            width: "100%",
            maxHeight: height,
            objectFit: "contain",
            display: "block",
            borderRadius: 14,
          }}
        />
      </div>
    </div>
  );

  const PhotoField = ({
    title,
    photoKey,
    value,
    alt,
    previewHeight = 260,
    referenceImage = "",
    referenceTitle = "Örnek Görsel",
    referenceHeight = 220,
  }) => {
    const inputId = `photo-${photoKey}`;

    return (
      <div style={photoItemStyle}>
        <label style={photoTitleStyle}>{title}</label>

        <input
          id={inputId}
          type="file"
          accept="image/*"
          onChange={(e) => handlePhotoChange(e, photoKey)}
          style={{ display: "none" }}
        />

        <label
          htmlFor={inputId}
          style={{
            display: "inline-block",
            width: "100%",
            boxSizing: "border-box",
            textAlign: "center",
            padding: "13px 14px",
            borderRadius: 14,
            border: "1px solid #d1d5db",
            background: "#111827",
            color: "#fff",
            fontWeight: 800,
            cursor: "pointer",
            marginBottom: 8,
          }}
        >
          Fotoğraf Seç
        </label>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: 14,
            fontWeight: 800,
            color: value ? "#166534" : "#991b1b",
            background: value ? "#dcfce7" : "#fee2e2",
            border: `1px solid ${value ? "#86efac" : "#fecaca"}`,
            borderRadius: 12,
            padding: "10px 12px",
            marginBottom: 12,
          }}
        >
          <span>{value ? "✅" : "⚠️"}</span>
          <span>{value ? "Fotoğraf seçildi" : "Fotoğraf bekleniyor"}</span>
        </div>

        {value && (
          <div style={previewWrapStyle}>
            <img
              src={value}
              alt={alt}
              style={{ ...previewImageStyle, maxHeight: previewHeight }}
            />
          </div>
        )}

        {referenceImage ? (
          <ReferenceImage
            src={referenceImage}
            title={referenceTitle}
            height={referenceHeight}
          />
        ) : null}
      </div>
    );
  };

  const pageStyle = {
    minHeight: "100vh",
    background: "#eef1f4",
    fontFamily: "SamsungSans, Arial, sans-serif",
  };

  const contentStyle = {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "22px 14px 40px",
  };

  const cardStyle = {
    background: "#fff",
    borderRadius: 22,
    boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
    padding: 18,
    marginTop: 16,
  };

  const labelStyle = {
    display: "block",
    fontWeight: 700,
    color: "#111827",
    marginBottom: 8,
    fontSize: 15,
  };

  const photoTitleStyle = {
    display: "block",
    fontFamily: "SamsungSans, Arial, sans-serif",
    fontWeight: 700,
    color: "#111827",
    marginBottom: 8,
    fontSize: 15,
  };

  const inputStyle = {
    width: "100%",
    padding: "14px 14px",
    borderRadius: 14,
    border: "1px solid #d1d5db",
    background: "#fff",
    fontSize: 15,
    boxSizing: "border-box",
    marginBottom: 16,
  };

  const fileInputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: 14,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    fontSize: 14,
    boxSizing: "border-box",
    marginBottom: 12,
  };

  const previewWrapStyle = {
    width: "100%",
    borderRadius: 18,
    overflow: "hidden",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    marginBottom: 10,
  };

  const previewImageStyle = {
    width: "100%",
    display: "block",
    objectFit: "cover",
  };

  const referenceWrapStyle = {
    marginTop: 6,
    borderRadius: 18,
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
    padding: 12,
  };

  const referenceTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
    marginBottom: 8,
  };

  const referenceImageBoxStyle = {
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    border: "1px solid #e5e7eb",
    padding: 8,
  };

  const buttonRowStyle = {
    display: "flex",
    gap: 12,
    marginTop: 8,
    alignItems: "center",
  };

  const backButtonStyle = {
    width: 52,
    height: 46,
    borderRadius: 14,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    fontWeight: 800,
    fontSize: 22,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const nextButtonStyle = {
    flex: 1,
    padding: "15px 16px",
    borderRadius: 14,
    border: "none",
    background: "#111827",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
  };

  const answerButton = (selected) => ({
    flex: 1,
    padding: "16px",
    borderRadius: 16,
    border: selected ? "2px solid #111827" : "1px solid #d1d5db",
    background: selected ? "#f3f4f6" : "#fff",
    color: "#111827",
    fontWeight: 700,
    fontSize: 16,
    cursor: "pointer",
  });

  const photoGridTwoStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 18,
    alignItems: "start",
  };

  const photoGridThreeStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 18,
    alignItems: "start",
  };

  const photoItemStyle = {
    background: "#fff",
  };

  const summaryBoxStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "12px 14px",
    background: "#f8fafc",
  };

  const renderAhuSharedInputFields = () => (
    <>
      <div style={photoGridTwoStyle}>
        <div>
          <label style={labelStyle}>Proje Adı</label>
          <input
            style={inputStyle}
            value={ahuProjectName}
            onChange={(e) => setAhuProjectName(e.target.value)}
            placeholder="Proje adını girin"
          />
        </div>

        <div>
          <label style={labelStyle}>Servis Adı</label>
          <input
            style={inputStyle}
            value={ahuServiceName}
            onChange={(e) => setAhuServiceName(e.target.value)}
            placeholder="Servis adını girin"
          />
        </div>
      </div>

      <div style={photoGridTwoStyle}>
        <div>
          <label style={labelStyle}>Tarih</label>
          <input
            style={inputStyle}
            type="date"
            value={ahuDate}
            onChange={(e) => setAhuDate(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Dış Ünite Modu</label>
          <select
            style={inputStyle}
            value={ahuOutdoorMode}
            onChange={(e) => setAhuOutdoorMode(e.target.value)}
          >
            <option value="Standart">Standart</option>
            <option value="Eco">Eco</option>
          </select>
        </div>
      </div>

      <div style={photoGridTwoStyle}>
        <div>
          <label style={labelStyle}>Toplam Soğutma Kapasitesi (kW)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuCoolingCapacity}
            onChange={(e) => setAhuCoolingCapacity(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Santral Debisi (m³/h)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuAirflow}
            onChange={(e) => setAhuAirflow(e.target.value)}
          />
        </div>
      </div>

      <div style={photoGridTwoStyle}>
        <div>
          <label style={labelStyle}>Hedef Evaporasyon Sıcaklığı (°C)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuEvapTemp}
            onChange={(e) => setAhuEvapTemp(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Superheat (°C)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuSuperheat}
            onChange={(e) => setAhuSuperheat(e.target.value)}
          />
        </div>
      </div>

      <div style={photoGridTwoStyle}>
        <div>
          <label style={labelStyle}>Subcool (°C)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuSubcool}
            onChange={(e) => setAhuSubcool(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Kondenzasyon Sıcaklığı (°C)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuCondTemp}
            onChange={(e) => setAhuCondTemp(e.target.value)}
          />
        </div>
      </div>

      <div style={photoGridThreeStyle}>
        <div>
          <label style={labelStyle}>Batarya Giriş Havası Sıcaklığı (°C)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuEnteringAirTemp}
            onChange={(e) => setAhuEnteringAirTemp(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>R410A Basınç Kaybı (kPa)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuPressureDrop}
            onChange={(e) => setAhuPressureDrop(e.target.value)}
          />
        </div>

        <div>
          <label style={labelStyle}>Hava Hızı (m/s)</label>
          <input
            style={inputStyle}
            type="number"
            value={ahuAirVelocity}
            onChange={(e) => setAhuAirVelocity(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>DX Devre Sayısı</label>
        <select
          style={inputStyle}
          value={ahuCircuitCount}
          onChange={(e) => setAhuCircuitCount(e.target.value)}
        >
          <option value="Otomatik">Otomatik</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
        </select>
      </div>
    </>
  );

  const renderAhuFieldCircuitEditor = () => {
    const isSharedConnection = ahuFieldConnectionType === "shared";

    return (
      <>
        <div style={photoGridTwoStyle}>
          <div>
            <label style={labelStyle}>Sahada Uygulanan Dış Ünite Modu</label>
            <select style={inputStyle} value={ahuFieldOutdoorMode} onChange={(e) => handleAhuFieldModeChange(e.target.value)}>
              <option value="Standart">Standart</option>
              <option value="Eco">Eco</option>
            </select>
          </div>

          <div style={{ ...summaryBoxStyle, marginBottom: 16 }}>
            <div style={{ color: "#64748b", fontSize: 12 }}>Devre Başına Limit</div>
            <div style={{ fontWeight: 900, color: "#111827", marginTop: 4 }}>
              {ahuFieldOutdoorMode === "Eco" ? "Eco: her devrede max 1 Eco dış ünite" : "Standart: her devrede max 4 dış ünite"}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Bağlantı Tipi</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button type="button" onClick={() => handleAhuFieldConnectionTypeChange("separate")} style={{ borderRadius: 14, border: `1px solid ${!isSharedConnection ? "#86efac" : "#cbd5e1"}`, background: !isSharedConnection ? "#dcfce7" : "#fff", color: !isSharedConnection ? "#166534" : "#111827", padding: "12px 14px", textAlign: "left", fontWeight: 900, cursor: "pointer" }}>
              Her devreye ayrı dış ünite
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, color: "#64748b" }}>Normal saha kaydı</div>
            </button>
            <button type="button" onClick={() => handleAhuFieldConnectionTypeChange("shared")} style={{ borderRadius: 14, border: `1px solid ${isSharedConnection ? "#fecaca" : "#cbd5e1"}`, background: isSharedConnection ? "#fee2e2" : "#fff", color: isSharedConnection ? "#991b1b" : "#111827", padding: "12px 14px", textAlign: "left", fontWeight: 900, cursor: "pointer" }}>
              Ortak dış ünite / çoklu devre
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4, color: "#64748b" }}>Tek gövde dış ünite birden fazla devreye bağlıysa</div>
            </button>
          </div>
        </div>

        {isSharedConnection ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ borderRadius: 14, border: "1px solid #fecaca", background: "#fff1f2", color: "#991b1b", padding: "12px 14px", fontWeight: 800, lineHeight: 1.5 }}>
              {FIELD_SHARED_CONNECTION_WARNING}
            </div>
            {ahuFieldSharedGroups.map((group, groupIndex) => {
              const selectedValue = group.unit ? `${group.unit.hp}|${group.unit.model}` : ahuFieldOutdoorOptions[0]?.value || "";
              const selectedCircuitNos = group.circuitNos || [];
              const groupHp = group.unit ? Number(group.unit.hp) || 0 : 0;
              const groupKw = groupHp * 3.5;
              const isMultiCircuit = selectedCircuitNos.length > 1;
              return (
                <div key={`shared-field-group-${groupIndex}`} style={{ borderRadius: 16, border: `1px solid ${isMultiCircuit ? "#fecaca" : "#e2e8f0"}`, background: isMultiCircuit ? "#fff7f7" : "#fff", padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 900, color: "#111827" }}>Ortak Dış Ünite Grubu {groupIndex + 1}</div>
                      <div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>Fiziksel dış ünite 1 kez sayılır: {formatNumber(groupHp)} HP / {formatNumber(groupKw)} kW</div>
                    </div>
                    <button type="button" onClick={() => removeAhuFieldSharedGroup(groupIndex)} style={{ border: "1px solid #fecaca", background: "#fff1f2", color: "#991b1b", borderRadius: 999, padding: "8px 12px", fontWeight: 900, cursor: "pointer" }}>Grubu Sil</button>
                  </div>

                  <label style={{ ...labelStyle, fontSize: 13 }}>Ortak Dış Ünite Modeli</label>
                  <select style={{ ...inputStyle, marginBottom: 12 }} value={selectedValue} onChange={(e) => updateAhuFieldSharedUnit(groupIndex, e.target.value)}>
                    {ahuFieldOutdoorOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                  </select>

                  <label style={{ ...labelStyle, fontSize: 13 }}>Bu dış ünite hangi devrelere bağlı?</label>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 8, marginBottom: 10 }}>
                    {Array.from({ length: FIELD_CIRCUIT_COUNT }, (_, index) => index + 1).map((circuitNo) => {
                      const checked = selectedCircuitNos.includes(circuitNo);
                      return (<button key={`shared-circuit-${groupIndex}-${circuitNo}`} type="button" onClick={() => toggleAhuFieldSharedCircuit(groupIndex, circuitNo)} style={{ borderRadius: 12, border: `1px solid ${checked ? "#2563eb" : "#cbd5e1"}`, background: checked ? "#dbeafe" : "#fff", color: checked ? "#1d4ed8" : "#334155", padding: "9px 8px", fontWeight: 900, cursor: "pointer" }}>{checked ? "✓ " : ""}Devre {circuitNo}</button>);
                    })}
                  </div>

                  {isMultiCircuit && (<div style={{ borderRadius: 12, border: "1px solid #fecaca", background: "#fee2e2", color: "#991b1b", padding: "10px 12px", fontWeight: 900, marginBottom: 10, lineHeight: 1.45 }}>UYGUN DEĞİL: Tek gövde dış ünitenin birden fazla bağımsız DX devreyi ortak beslemesi uygun değildir.</div>)}

                  {selectedCircuitNos.length > 0 ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <div>
                        <label style={{ ...labelStyle, fontSize: 13 }}>Sahada Kullanılan EEV Adedi</label>
                        <input
                          style={{ ...inputStyle, marginBottom: 0 }}
                          type="number"
                          min="0"
                          value={group.eev || ""}
                          onChange={(e) => updateAhuFieldSharedEev(groupIndex, e.target.value)}
                          placeholder={groupHp > 0 ? `Tek gövde önerisi: ${Math.ceil(groupHp / 10)}` : "Kullanılan toplam EEV adedini girin"}
                        />
                      </div>
                      <div style={{ borderRadius: 12, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#475569", padding: "10px 12px", fontWeight: 700, fontSize: 13, lineHeight: 1.45 }}>
                        Bu bağlantı tipinde EEV devre başına ayrılmaz. Tek gövde dış ünite için sahada kullanılan toplam EEV adedi girilir.
                      </div>
                    </div>
                  ) : (<div style={{ borderRadius: 12, background: "#f8fafc", border: "1px dashed #cbd5e1", color: "#64748b", padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>Bu ortak dış ünite için bağlı devre seçilmedi.</div>)}
                </div>
              );
            })}
            <button type="button" onClick={addAhuFieldSharedGroup} style={{ border: "1px dashed #94a3b8", background: "#fff", borderRadius: 14, padding: "12px 14px", fontWeight: 900, color: "#334155", cursor: "pointer" }}>+ Ortak Dış Ünite Grubu Ekle</button>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {ahuFieldCircuits.map((circuit, circuitIndex) => {
              const circuitTotalHp = circuit.units.reduce((sum, unit) => sum + (Number(unit.hp) || 0), 0);
              const circuitTotalKw = circuitTotalHp * 3.5;
              const recommendedEev = circuitTotalHp > 0 ? Math.ceil(circuitTotalHp / 10) : 0;
              const maxUnits = getAhuFieldMaxUnits();
              const canAddUnit = circuit.units.length < maxUnits;
              return (
                <div key={`field-circuit-page-${circuitIndex}`} style={{ borderRadius: 16, border: "1px solid #e2e8f0", background: "#fff", padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                    <div><div style={{ fontWeight: 900, color: "#111827" }}>Devre {circuitIndex + 1}</div><div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>Toplam: {formatNumber(circuitTotalHp)} HP / {formatNumber(circuitTotalKw)} kW{circuitTotalHp > 0 ? ` • Önerilen EEV: ${recommendedEev}` : ""}</div></div>
                    <button type="button" onClick={() => addAhuFieldUnit(circuitIndex)} disabled={!canAddUnit} style={{ border: "none", borderRadius: 999, padding: "8px 12px", background: canAddUnit ? "#111827" : "#e5e7eb", color: canAddUnit ? "#fff" : "#94a3b8", fontWeight: 900, cursor: canAddUnit ? "pointer" : "not-allowed" }}>+ Dış Ünite Ekle</button>
                  </div>
                  {circuit.units.length > 0 ? (<div style={{ display: "grid", gap: 8 }}>{circuit.units.map((unit, unitIndex) => (<div key={`field-page-unit-${circuitIndex}-${unitIndex}`} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}><select style={{ ...inputStyle, marginBottom: 0 }} value={`${unit.hp}|${unit.model}`} onChange={(e) => updateAhuFieldUnit(circuitIndex, unitIndex, e.target.value)}>{ahuFieldOutdoorOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}</select><button type="button" onClick={() => removeAhuFieldUnit(circuitIndex, unitIndex)} style={{ width: 38, height: 38, borderRadius: 12, border: "1px solid #fecaca", background: "#fff1f2", color: "#991b1b", fontWeight: 900, cursor: "pointer" }}>×</button></div>))}</div>) : (<div style={{ borderRadius: 12, background: "#f8fafc", border: "1px dashed #cbd5e1", color: "#64748b", padding: "10px 12px", fontWeight: 700, fontSize: 13 }}>Bu devre için sahada uygulanan dış ünite seçilmedi.</div>)}
                  <div style={{ marginTop: 10 }}><label style={{ ...labelStyle, fontSize: 13 }}>Sahada Kullanılan EEV Adedi</label><input style={{ ...inputStyle, marginBottom: 0 }} type="number" min="0" value={circuit.eev} onChange={(e) => updateAhuFieldCircuitEev(circuitIndex, e.target.value)} placeholder={circuitTotalHp > 0 ? `Öneri: ${recommendedEev}` : "Bu devrede EEV varsa girin"} /></div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Saha Notu / Açıklama</label>
          <textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical", marginBottom: 0 }} value={ahuFieldNote} onChange={(e) => setAhuFieldNote(e.target.value)} placeholder="Sahada uygulanan özel durum, geçmiş uygulama notu vb." />
        </div>
      </>
    );
  };

  const renderAhuFieldResultCard = () => (
    <div
      style={{
        borderRadius: 18,
        padding: "16px 18px",
        background: ahuFieldComparison.allOk ? "#dcfce7" : "#fee2e2",
        border: `1px solid ${ahuFieldComparison.allOk ? "#86efac" : "#fecaca"}`,
        color: ahuFieldComparison.allOk ? "#166534" : "#991b1b",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.95 }}>KARŞILAŞTIRMA SONUCU</div>
      <div style={{ fontSize: 20, fontWeight: 900, marginTop: 5 }}>
        {ahuFieldComparison.allOk ? "UYGUN" : "UYGUN DEĞİL / KONTROL GEREKİYOR"}
      </div>

      {ahuFieldComparison.allOk ? (
        <div style={{ fontSize: 14, fontWeight: 700, marginTop: 10, lineHeight: 1.6 }}>
          Olması gereken hesap ile sahada uygulanan seçim uyumlu.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          {(ahuFieldComparison.issues.length > 0
            ? ahuFieldComparison.issues
            : [ahuFieldComparison.summary]
          ).map((issue, index) => (
            <div
              key={`field-issue-${index}`}
              style={{
                borderRadius: 12,
                background: "rgba(255,255,255,0.65)",
                border: "1px solid rgba(153,27,27,0.2)",
                padding: "9px 11px",
                fontWeight: 800,
                lineHeight: 1.45,
              }}
            >
              {index + 1}. {issue}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAhuFieldInputSummaryCard = () => {
    const summaryRows = [
      ["Proje", ahuProjectName || "-"],
      ["Servis", ahuServiceName || "-"],
      ["Tarih", ahuDate || "-"],
      ["Dış Ünite Modu", ahuOutdoorMode || "-"],
      ["Kapasite", `${ahuCoolingCapacity || "-"} kW`],
      ["Debi", `${ahuAirflow || "-"} m³/h`],
      ["DX Devre", ahuCircuitCount === "Otomatik" ? `Otomatik (${ahuCalc.circuitCount})` : ahuCircuitCount],
      ["Bağlantı", ahuFieldComparison.connectionTypeLabel || "-"],
      ["Evaporasyon", `${ahuEvapTemp || "-"} °C`],
      ["Superheat", `${ahuSuperheat || "-"} °C`],
      ["Subcool", `${ahuSubcool || "-"} °C`],
      ["Kondenzasyon", `${ahuCondTemp || "-"} °C`],
      ["Batarya Giriş Havası", `${ahuEnteringAirTemp || "-"} °C`],
      ["R410A Basınç Kaybı", `${ahuPressureDrop || "-"} kPa`],
      ["Hava Hızı", `${ahuAirVelocity || "-"} m/s`],
    ];

    return (
      <>
        <div style={{ fontWeight: 900, fontSize: 18, color: "#111827", marginBottom: 14 }}>
          Girilen Değerler
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 8,
          }}
        >
          {summaryRows.map(([label, value]) => (
            <div
              key={label}
              style={{
                borderRadius: 12,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "9px 10px",
              }}
            >
              <div style={{ color: "#64748b", fontSize: 11, fontWeight: 800 }}>{label}</div>
              <div style={{ color: "#111827", fontSize: 13, fontWeight: 900, marginTop: 3 }}>{value}</div>
            </div>
          ))}
        </div>
      </>
    );
  };


    return (
    <div style={pageStyle}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: "#000",
          borderBottom: "1px solid #1f2937",
          boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            minHeight: 64,
            padding: "0 14px",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <img
              src={samsungLogo}
              alt="Samsung"
              style={{
                height: 18,
                width: "auto",
                objectFit: "contain",
                display: "block",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 18,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <span
              onClick={() => setActivePage("compressor")}
              style={{
                cursor: "pointer",
                color: activePage === "compressor" ? "#fff" : "#9ca3af",
                fontWeight: 800,
                fontSize: 18,
              }}
            >
              Kompresör Kontrol
            </span>

            <span
              style={{
                color: "#4b5563",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              |
            </span>

            <span
              onClick={() => setActivePage("ahu")}
              style={{
                cursor: "pointer",
                color: activePage === "ahu" || activePage === "ahuFieldCompare" ? "#fff" : "#9ca3af",
                fontWeight: 800,
                fontSize: 18,
              }}
            >
              AHU Kontrol
            </span>
          </div>

          <div
            style={{
              color: "#d1d5db",
              fontSize: 13,
              textAlign: "right",
              fontWeight: 500,
            }}
          >
            Klima Sistemleri
          </div>
        </div>
      </div>

      <div style={contentStyle}>
        {activePage === "compressor" && (
          <>
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <h2
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: "#111827",
                  margin: "8px 0 0",
                }}
              >
                {pageTitle[step]}
              </h2>
            </div>

            <div style={cardStyle}>
              {step === 1 && (
                <div
                  style={{
                    maxWidth: 620,
                    margin: "0 auto",
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    borderRadius: 22,
                    padding: 24,
                    boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                  }}
                >
                  <div style={{ textAlign: "center", marginBottom: 22 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: "#64748b",
                        fontWeight: 700,
                        letterSpacing: "0.3px",
                        marginBottom: 6,
                      }}
                    >
                      KONTROL FORMU GİRİŞİ
                    </div>
                    <div
                      style={{
                        fontSize: 26,
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      Genel Bilgiler
                    </div>
                  </div>

                  <label style={labelStyle}>Servis Adı / Firma</label>
                  <input
                    style={inputStyle}
                    value={serviceCompany}
                    onChange={(e) => setServiceCompany(e.target.value)}
                    placeholder="Servis adı veya firma adını girin"
                  />

                  <label style={labelStyle}>Servis Personeli</label>
                  <input
                    style={inputStyle}
                    value={servicePerson}
                    onChange={(e) => setServicePerson(e.target.value)}
                    placeholder="Servis personeli adını girin"
                  />

                  <label style={labelStyle}>Tarih</label>
                  <input
                    style={inputStyle}
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                  />

                  <label style={labelStyle}>Müşteri Adı</label>
                  <input
                    style={{ ...inputStyle, marginBottom: 0 }}
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Müşteri adını girin"
                  />
                </div>
              )}

              {step === 2 && (
                <PhotoField
                  title="Cihaz Model Etiketi Fotoğrafı"
                  photoKey="modelSerialLabel"
                  value={photos.modelSerialLabel}
                  alt="Cihaz model etiketi"
                  previewHeight={340}
                  referenceImage={sampleProductLabel}
                  referenceTitle="Sabit Referans Görsel"
                  referenceHeight={380}
                />
              )}

              {step === 3 && (
                <>
                  <div style={photoGridTwoStyle}>
                    <PhotoField
                      title="Eski Kompresör Model Etiketi Fotoğrafı"
                      photoKey="oldCompLabel"
                      value={photos.oldCompLabel}
                      alt="Eski kompresör model etiketi"
                      previewHeight={280}
                    />
                    <PhotoField
                      title="Yeni Kompresör Model Etiketi Fotoğrafı"
                      photoKey="newCompLabel"
                      value={photos.newCompLabel}
                      alt="Yeni kompresör model etiketi"
                      previewHeight={280}
                    />
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <ReferenceImage
                      src={sampleCompressorLabel}
                      title="Sabit Referans Görsel"
                      height={260}
                    />
                  </div>
                </>
              )}

              {step === 4 && (
                <>
                  <label style={labelStyle}>Kompresör Modeli</label>
                  <select
                    style={inputStyle}
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                  >
                    <option value="">Model seçiniz</option>
                    {Object.keys(compressorData).map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>

                  <label style={labelStyle}>Sıcaklık (°C)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    value={temperature}
                    onChange={(e) => setTemperature(e.target.value)}
                    placeholder="Örn: 55"
                  />

                  <label style={labelStyle}>U-W Ölçüm Değeri</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="decimal"
                    value={measuredUW}
                    onChange={(e) => setMeasuredUW(e.target.value)}
                  />

                  <label style={labelStyle}>U-V Ölçüm Değeri</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="decimal"
                    value={measuredUV}
                    onChange={(e) => setMeasuredUV(e.target.value)}
                  />

                  <label style={labelStyle}>W-V Ölçüm Değeri</label>
                  <input
                    style={inputStyle}
                    type="text"
                    inputMode="decimal"
                    value={measuredWV}
                    onChange={(e) => setMeasuredWV(e.target.value)}
                  />

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                    {renderResultCard("U-W", uwStatus)}
                    {renderResultCard("U-V", uvStatus)}
                    {renderResultCard("W-V", wvStatus)}
                  </div>

                  <div
                    style={{
                      borderRadius: 16,
                      padding: "16px 14px",
                      marginBottom: 18,
                      background: mohmOverall ? "#16a34a" : "#dc2626",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 16,
                      textAlign: "center",
                    }}
                  >
                    {mohmOverall ? "GENEL SONUÇ: UYGUN" : "GENEL SONUÇ: UYGUN DEĞİL"}
                  </div>

                  <div style={photoGridThreeStyle}>
                    <PhotoField
                      title="U-W Ölçüm Fotoğrafı"
                      photoKey="mohmUW"
                      value={photos.mohmUW}
                      alt="U-W ölçüm"
                      previewHeight={220}
                      referenceImage={sampleMohmUW}
                      referenceTitle="Sabit Referans U-W"
                      referenceHeight={180}
                    />
                    <PhotoField
                      title="U-V Ölçüm Fotoğrafı"
                      photoKey="mohmUV"
                      value={photos.mohmUV}
                      alt="U-V ölçüm"
                      previewHeight={220}
                      referenceImage={sampleMohmUV}
                      referenceTitle="Sabit Referans U-V"
                      referenceHeight={180}
                    />
                    <PhotoField
                      title="W-V Ölçüm Fotoğrafı"
                      photoKey="mohmWV"
                      value={photos.mohmWV}
                      alt="W-V ölçüm"
                      previewHeight={220}
                      referenceImage={sampleMohmWV}
                      referenceTitle="Sabit Referans W-V"
                      referenceHeight={180}
                    />
                  </div>
                </>
              )}

              {step === 5 && (
                <>
                  <div style={photoGridTwoStyle}>
                    <PhotoField
                      title="Eski Kompresör Terazi Fotoğrafı"
                      photoKey="oldCompWeight"
                      value={photos.oldCompWeight}
                      alt="Eski kompresör terazi"
                      previewHeight={260}
                    />
                    <PhotoField
                      title="Yeni Kompresör Terazi Fotoğrafı"
                      photoKey="newCompWeight"
                      value={photos.newCompWeight}
                      alt="Yeni kompresör terazi"
                      previewHeight={260}
                    />
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <ReferenceImage
                      src={sampleWeightControl}
                      title="Sabit Referans Görsel"
                      height={260}
                    />
                  </div>
                </>
              )}

              {step === 6 && (
                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() => {
                      setMechanicalSound("evet");
                      finalizeTechnicalSupport();
                    }}
                    style={answerButton(mechanicalSound === "evet")}
                  >
                    Mekanik Ses Var
                  </button>
                  <button
                    onClick={() => {
                      setMechanicalSound("hayir");
                      setStep(7);
                    }}
                    style={answerButton(mechanicalSound === "hayir")}
                  >
                    Mekanik Ses Yok
                  </button>
                </div>
              )}

              {step === 7 && (
                <div style={{ display: "flex", gap: 12, flexDirection: "column" }}>
                  <button
                    onClick={() => {
                      setWeightStatus("800gr-ağır");
                      setStep(8);
                    }}
                    style={answerButton(weightStatus === "800gr-ağır")}
                  >
                    Yeni kompresör, sökülen kompresörden 800 gram daha ağır
                  </button>
                  <button
                    onClick={() => {
                      setWeightStatus("eşit-veya-eski-daha-ağır");
                      setStep(11);
                    }}
                    style={answerButton(weightStatus === "eşit-veya-eski-daha-ağır")}
                  >
                    Sökülen kompresör, yenisiyle aynı ağırlıkta veya daha ağır
                  </button>
                </div>
              )}

              {step === 8 && (
                <>
                  <div style={photoGridTwoStyle}>
                    <PhotoField
                      title="ARV Fotoğrafı"
                      photoKey="arvPhoto"
                      value={photos.arvPhoto}
                      alt="ARV"
                      previewHeight={220}
                      referenceImage={sampleArvSeparator}
                      referenceTitle="Sabit Referans Görsel 1"
                      referenceHeight={180}
                    />
                    <PhotoField
                      title="Yağ Separatörü Fotoğrafı"
                      photoKey="separatorPhoto"
                      value={photos.separatorPhoto}
                      alt="Yağ separatörü"
                      previewHeight={220}
                      referenceImage={sampleSeparatorDetail}
                      referenceTitle="Sabit Referans Görsel 2"
                      referenceHeight={180}
                    />
                  </div>

                  <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                    <button
                      onClick={() => {
                        setValveSeparatorIssue("evet");
                        finalizeValveReplacement();
                      }}
                      style={answerButton(valveSeparatorIssue === "evet")}
                    >
                      Sorun Var
                    </button>
                    <button
                      onClick={() => {
                        setValveSeparatorIssue("hayir");
                        setStep(9);
                      }}
                      style={answerButton(valveSeparatorIssue === "hayir")}
                    >
                      Sorun Yok
                    </button>
                  </div>
                </>
              )}

              {step === 9 && (
                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() => {
                      setOilCameFromValves("evet");
                      setStep(10);
                    }}
                    style={answerButton(oilCameFromValves === "evet")}
                  >
                    Yağ Çıktı
                  </button>
                  <button
                    onClick={() => {
                      setOilCameFromValves("hayir");
                      finalizeOilTrap();
                    }}
                    style={answerButton(oilCameFromValves === "hayir")}
                  >
                    Yağ Çıkmadı
                  </button>
                </div>
              )}

              {step === 10 && (
                <>
                  <PhotoField
                    title="ARV / Separatör Yağ Rengi Fotoğrafı"
                    photoKey="oilColorValvePhoto"
                    value={photos.oilColorValvePhoto}
                    alt="ARV / Separatör yağ rengi"
                    previewHeight={260}
                    referenceImage={sampleOilColors}
                    referenceTitle="Yağ Renk Skalası"
                    referenceHeight={220}
                  />
                  <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                    <button
                      onClick={() => {
                        setOilColorAfterValve("evet");
                        finalizeDirectCompressorChange800();
                      }}
                      style={answerButton(oilColorAfterValve === "evet")}
                    >
                      Uygun
                    </button>
                    <button
                      onClick={() => {
                        setOilColorAfterValve("hayir");
                        finalizeFullOilRenewal800();
                      }}
                      style={answerButton(oilColorAfterValve === "hayir")}
                    >
                      Uygun Değil
                    </button>
                  </div>
                </>
              )}

              {step === 11 && (
                <>
                  <PhotoField
                    title="Sökülen Kompresör Yağ Rengi Fotoğrafı"
                    photoKey="mainOilColorPhoto"
                    value={photos.mainOilColorPhoto}
                    alt="Sökülen kompresör yağ rengi"
                    previewHeight={260}
                    referenceImage={sampleOilColors}
                    referenceTitle="Yağ Renk Skalası"
                    referenceHeight={220}
                  />
                  <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                    <button
                      onClick={() => {
                        setMainOilColorSuitable("evet");
                        finalizeDirectCompressorChangeNormal();
                      }}
                      style={answerButton(mainOilColorSuitable === "evet")}
                    >
                      Uygun
                    </button>
                    <button
                      onClick={() => {
                        setMainOilColorSuitable("hayir");
                        setStep(12);
                      }}
                      style={answerButton(mainOilColorSuitable === "hayir")}
                    >
                      Uygun Değil
                    </button>
                  </div>
                </>
              )}

              {step === 12 && (
                <>
                  <PhotoField
                    title="Diğer Kompresör / Akümülatör Yağ Rengi Fotoğrafı"
                    photoKey="otherCompressorsOilPhoto"
                    value={photos.otherCompressorsOilPhoto}
                    alt="Diğer kompresör / akümülatör yağ rengi"
                    previewHeight={260}
                    referenceImage={sampleOilColors}
                    referenceTitle="Yağ Renk Skalası"
                    referenceHeight={220}
                  />
                  <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                    <button
                      onClick={() => {
                        setOtherCompressorsOilSuitable("evet");
                        finalizeLocalIssue();
                      }}
                      style={answerButton(otherCompressorsOilSuitable === "evet")}
                    >
                      Uygun
                    </button>
                    <button
                      onClick={() => {
                        setOtherCompressorsOilSuitable("hayir");
                        finalizeFullOilRenewalNormal();
                      }}
                      style={answerButton(otherCompressorsOilSuitable === "hayir")}
                    >
                      Uygun Değil
                    </button>
                  </div>
                </>
              )}

              {step === 13 && (
                <div>
                  <div style={{ fontWeight: 800, fontSize: 20, color: "#111827", marginBottom: 16 }}>
                    {flowSummary.finalTitle}
                  </div>
                  <div style={{ fontSize: 16, color: "#374151", marginBottom: 16, lineHeight: 1.6 }}>
                    {flowSummary.finalText}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, color: "#111827", marginBottom: 8 }}>Karar Ağacı:</div>
                    <div style={{ color: "#6b7280", lineHeight: 1.6 }}>{flowSummary.path}</div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, color: "#111827", marginBottom: 8 }}>Önerilen İşlemler:</div>
                    <ul style={{ color: "#374151", lineHeight: 1.6 }}>
                      {flowSummary.actions.map((action, index) => (
                        <li key={index}>{action}</li>
                      ))}
                    </ul>
                  </div>
                  {flowSummary.detailRows.length > 0 && (
                    <div>
                      <div style={{ fontWeight: 800, color: "#111827", marginBottom: 8 }}>Kontrol Özeti:</div>
                      <ul style={{ color: "#6b7280", lineHeight: 1.6 }}>
                        {flowSummary.detailRows.map((row, index) => (
                          <li key={index}>{row.label}: {row.value}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button
                    onClick={generatePDF}
                    style={{
                      width: "100%",
                      padding: "16px",
                      borderRadius: 14,
                      border: "none",
                      background: "#111827",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: 16,
                      cursor: "pointer",
                      marginTop: 20,
                    }}
                  >
                    PDF Oluştur
                  </button>
                </div>
              )}

              <div style={buttonRowStyle}>
                {step > 1 ? (
                  <button style={backButtonStyle} onClick={goBack}>
                    ‹
                  </button>
                ) : (
                  <div style={{ width: 52 }} />
                )}

                {step < 6 && (
                  <button style={nextButtonStyle} onClick={goNext}>
                    İleri
                  </button>
                )}
              </div>
            </div>
          </>
        )}


        {activePage === "ahuFieldCompare" && (
          <>
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <h2
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: "#111827",
                  margin: "8px 0 0",
                }}
              >
                Saha Uygulaması Karşılaştırması
              </h2>
              <div style={{ color: "#64748b", fontWeight: 700, marginTop: 6 }}>
                Bu sayfa sadece sahada uygulanmış dış ünite seçimini olması gereken hesapla karşılaştırır.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginTop: 16,
              }}
            >
              <button
                type="button"
                onClick={() => setActivePage("ahu")}
                style={{
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  color: "#111827",
                  borderRadius: 999,
                  padding: "10px 14px",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                ← AHU Kontrol Sayfasına Dön
              </button>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={generateAhuFieldPDF}
                  style={{
                    border: "none",
                    background: "#111827",
                    color: "#fff",
                    borderRadius: 999,
                    padding: "11px 16px",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  PDF Çıktısı Oluştur
                </button>

                <button
                  type="button"
                  onClick={generateAhuFieldPPT}
                  style={{
                    border: "1px solid #111827",
                    background: "#fff",
                    color: "#111827",
                    borderRadius: 999,
                    padding: "11px 16px",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  PPT Çıktısı Oluştur
                </button>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.05fr 1fr",
                gap: 18,
                alignItems: "start",
                marginTop: 16,
              }}
            >
              <div style={{ display: "grid", gap: 16 }}>
                <div style={cardStyle}>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: 18,
                      color: "#111827",
                      marginBottom: 14,
                    }}
                  >
                    Ortak AHU Girişleri
                  </div>
                  <div style={{ color: "#64748b", fontWeight: 700, fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
                    Bu alanlardaki değerler AHU kontrol hesabı ile aynıdır. Burada değiştirdiğinde AHU tarafındaki hesap da aynı değerlerle güncellenir.
                  </div>
                  {renderAhuSharedInputFields()}
                </div>

                <div style={cardStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                      marginBottom: 14,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 18, color: "#111827" }}>
                        Sahada Uygulanan Devreler
                      </div>
                      <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700, marginTop: 4 }}>
                        + tuşu ile her devreye sahada takılmış dış üniteleri seç.
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={clearAhuFieldComparison}
                      style={{
                        border: "1px solid #cbd5e1",
                        background: "#fff",
                        borderRadius: 999,
                        padding: "8px 12px",
                        fontWeight: 800,
                        color: "#334155",
                        cursor: "pointer",
                      }}
                    >
                      Temizle
                    </button>
                  </div>

                  {renderAhuFieldCircuitEditor()}
                </div>
              </div>

              <div style={{ display: "grid", gap: 16 }}>
                <div style={cardStyle}>{renderAhuFieldResultCard()}</div>

                <div style={cardStyle}>{renderAhuFieldInputSummaryCard()}</div>

                <div style={cardStyle}>
                  <div style={{ fontWeight: 900, fontSize: 18, color: "#111827", marginBottom: 14 }}>
                    Olması Gereken Hesap Özeti
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Seçim Kriteri</div>
                      <div style={{ fontWeight: 900, color: "#111827", marginTop: 4 }}>
                        {ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.label || "-" : "-"}
                      </div>
                      <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                        {ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.reason || "-" : "Kapasite veya debi girilmelidir."}
                      </div>
                    </div>

                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Olması Gereken Toplam Dış Ünite</div>
                      <div style={{ fontWeight: 900, color: "#111827", marginTop: 4 }}>
                        {ahuFieldComparison.hasExpectedSelection
                          ? `${formatNumber(ahuFieldComparison.expectedHp)} HP / ${formatNumber(ahuFieldComparison.expectedKw)} kW`
                          : "Seçim bekleniyor"}
                      </div>
                    </div>

                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Olması Gereken Devre / EEV</div>
                      <div style={{ fontWeight: 900, color: "#111827", marginTop: 4 }}>
                        {ahuFieldComparison.hasExpectedSelection
                          ? `${ahuFieldComparison.expectedCircuitCount} devre / ${ahuFieldComparison.expectedEev} EEV`
                          : "-"}
                      </div>
                    </div>

                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Seçilen Dış Ünite Debi Aralığı</div>
                      <div style={{ fontWeight: 900, color: "#111827", marginTop: 4 }}>
                        {ahuFieldComparison.hasExpectedSelection
                          ? `${formatNumber(ahuFieldComparison.expectedMinAirflow)} – ${formatNumber(ahuFieldComparison.expectedMaxAirflow)} m³/h`
                          : "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {ahuFieldComparison.appliedDisplayBlocks.length > 0 && (
                  <div style={cardStyle}>
                    <div style={{ fontWeight: 900, fontSize: 18, color: "#111827", marginBottom: 14 }}>
                      Sahada Uygulanan Özet
                    </div>
                    <div style={{ display: "grid", gap: 10 }}>
                      {ahuFieldComparison.appliedDisplayBlocks.map((block, index) => (
                        <div key={`applied-page-block-${index}`} style={summaryBoxStyle}>
                          <div style={{ fontWeight: 900, color: "#111827", marginBottom: 8 }}>
                            {block.title} — {block.subtitle}
                          </div>
                          <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.6 }}>
                            {block.lines.map((line, lineIndex) => (
                              <div key={`applied-page-line-${index}-${lineIndex}`}>{line}</div>
                            ))}
                            {block.warning ? (
                              <div style={{ marginTop: 8, color: "#991b1b", fontWeight: 900 }}>
                                Uyarı: {block.warning}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={cardStyle}>
                  <div style={{ fontWeight: 900, fontSize: 18, color: "#111827", marginBottom: 14 }}>
                    Karşılaştırma Kriterleri
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {ahuFieldComparison.rows.map((row) => (
                      <div
                        key={`field-page-row-${row.label}`}
                        style={{
                          borderRadius: 14,
                          border: `1px solid ${row.ok ? "#86efac" : "#fecaca"}`,
                          background: row.ok ? "#f0fdf4" : "#fff1f2",
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 10,
                            alignItems: "flex-start",
                            marginBottom: 8,
                          }}
                        >
                          <div style={{ fontWeight: 900, color: "#111827" }}>{row.label}</div>
                          <div
                            style={{
                              borderRadius: 999,
                              padding: "5px 9px",
                              fontSize: 11,
                              fontWeight: 900,
                              background: row.ok ? "#dcfce7" : "#fee2e2",
                              color: row.ok ? "#166534" : "#991b1b",
                              border: `1px solid ${row.ok ? "#86efac" : "#fecaca"}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.ok ? "UYGUN" : "UYGUN DEĞİL"}
                          </div>
                        </div>

                        <div style={{ display: "grid", gap: 6, color: "#334155", fontSize: 13, lineHeight: 1.5 }}>
                          <div><strong>Olması Gereken:</strong> {row.expected}</div>
                          <div><strong>Sahada Uygulanan:</strong> {row.applied}</div>
                          <div style={{ color: row.ok ? "#166534" : "#991b1b", fontWeight: 800 }}>
                            Açıklama: {row.reason}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activePage === "ahu" && (
          <>
            <div style={{ textAlign: "center", marginTop: 8 }}>
              <h2
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: "#111827",
                  margin: "8px 0 0",
                }}
              >
                AHU Kontrol
              </h2>
            </div>

            <div style={cardStyle}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                      color: "#111827",
                      marginBottom: 14,
                    }}
                  >
                    DX Batarya Yerleşim Kuralı
                  </div>

                  <div
                    style={{
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      padding: 12,
                    }}
                  >
                    <img
                      src={dxBataryaImg}
                      alt="DX Batarya Yerleşim Kuralı"
                      style={{
                        width: "100%",
                        display: "block",
                        objectFit: "contain",
                        borderRadius: 8,
                      }}
                    />
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      padding: "12px 14px",
                      borderRadius: 12,
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      color: "#9a3412",
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    Dikkat: DX Batarya-3 gibi 3 bataryanın seri yerleşimi uygun değildir. Hava akış yönüne göre interlaced veya parallel düzen tercih edilmelidir.
                  </div>
            </div>


            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.1fr 1fr",
                gap: 18,
                alignItems: "start",
                marginTop: 16,
              }}
            >
              <div style={{ ...cardStyle, position: "relative" }}>
                <button
                  type="button"
                  aria-label="Saha uygulaması karşılaştırma modu"
                  onClick={() => { setAhuFieldCompareEnabled(true); setActivePage("ahuFieldCompare"); }}
                  style={{
                    position: "absolute",
                    top: -28,
                    right: -38,
                    width: 58,
                    height: 58,
                    border: "none",
                    background: "transparent",
                    opacity: 0,
                    cursor: "pointer",
                    padding: 0,
                    zIndex: 5,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                      color: "#111827",
                    }}
                  >
                    Giriş Parametreleri
                  </div>

                </div>

                <div style={photoGridTwoStyle}>
                  <div>
                    <label style={labelStyle}>Proje Adı</label>
                    <input
                      style={inputStyle}
                      value={ahuProjectName}
                      onChange={(e) => setAhuProjectName(e.target.value)}
                      placeholder="Proje adını girin"
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Servis Adı</label>
                    <input
                      style={inputStyle}
                      value={ahuServiceName}
                      onChange={(e) => setAhuServiceName(e.target.value)}
                      placeholder="Servis adını girin"
                    />
                  </div>
                </div>

                <div style={photoGridTwoStyle}>
                  <div>
                    <label style={labelStyle}>Tarih</label>
                    <input
                      style={inputStyle}
                      type="date"
                      value={ahuDate}
                      onChange={(e) => setAhuDate(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Dış Ünite Modu</label>
                    <select
                      style={inputStyle}
                      value={ahuOutdoorMode}
                      onChange={(e) => setAhuOutdoorMode(e.target.value)}
                    >
                      <option value="Standart">Standart</option>
                      <option value="Eco">Eco</option>
                    </select>
                  </div>
                </div>

                <div style={photoGridTwoStyle}>
                  <div>
                    <label style={labelStyle}>Toplam Soğutma Kapasitesi (kW)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuCoolingCapacity}
                      onChange={(e) => setAhuCoolingCapacity(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Santral Debisi (m³/h)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuAirflow}
                      onChange={(e) => setAhuAirflow(e.target.value)}
                    />
                  </div>
                </div>

                <div style={photoGridTwoStyle}>
                  <div>
                    <label style={labelStyle}>Hedef Evaporasyon Sıcaklığı (°C)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuEvapTemp}
                      onChange={(e) => setAhuEvapTemp(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Superheat (°C)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuSuperheat}
                      onChange={(e) => setAhuSuperheat(e.target.value)}
                    />
                  </div>
                </div>

                <div style={photoGridTwoStyle}>
                  <div>
                    <label style={labelStyle}>Subcool (°C)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuSubcool}
                      onChange={(e) => setAhuSubcool(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Kondenzasyon Sıcaklığı (°C)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuCondTemp}
                      onChange={(e) => setAhuCondTemp(e.target.value)}
                    />
                  </div>
                </div>

                <div style={photoGridThreeStyle}>
                  <div>
                    <label style={labelStyle}>Batarya Giriş Havası Sıcaklığı (°C)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuEnteringAirTemp}
                      onChange={(e) => setAhuEnteringAirTemp(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>R410A Basınç Kaybı (kPa)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuPressureDrop}
                      onChange={(e) => setAhuPressureDrop(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Hava Hızı (m/s)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      value={ahuAirVelocity}
                      onChange={(e) => setAhuAirVelocity(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>DX Devre Sayısı</label>
                  <select
                    style={inputStyle}
                    value={ahuCircuitCount}
                    onChange={(e) => setAhuCircuitCount(e.target.value)}
                  >
                    <option value="Otomatik">Otomatik</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>

                {false && ahuFieldCompareEnabled && (
                  <div
                    style={{
                      marginTop: 18,
                      borderRadius: 18,
                      border: "1px solid #cbd5e1",
                      background: "#f8fafc",
                      padding: 14,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 900, color: "#111827", fontSize: 16 }}>
                          Saha Uygulaması Karşılaştırma Modu
                        </div>
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                          Sahada geçmişte uygulanmış dış üniteleri devre devre seç. Arka planda olması gereken hesap değişmeden çalışır.
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={clearAhuFieldComparison}
                        style={{
                          border: "1px solid #cbd5e1",
                          background: "#fff",
                          borderRadius: 999,
                          padding: "8px 12px",
                          fontWeight: 800,
                          color: "#334155",
                          cursor: "pointer",
                        }}
                      >
                        Temizle
                      </button>
                    </div>

                    <div style={photoGridTwoStyle}>
                      <div>
                        <label style={labelStyle}>Sahada Uygulanan Dış Ünite Modu</label>
                        <select
                          style={inputStyle}
                          value={ahuFieldOutdoorMode}
                          onChange={(e) => handleAhuFieldModeChange(e.target.value)}
                        >
                          <option value="Standart">Standart</option>
                          <option value="Eco">Eco</option>
                        </select>
                      </div>

                      <div style={{ ...summaryBoxStyle, marginBottom: 16 }}>
                        <div style={{ color: "#64748b", fontSize: 12 }}>Devre Başına Limit</div>
                        <div style={{ fontWeight: 900, color: "#111827", marginTop: 4 }}>
                          {ahuFieldOutdoorMode === "Eco"
                            ? "Her devrede max 1 Eco dış ünite"
                            : "Her devrede max 4 Standart dış ünite"}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 12 }}>
                      {ahuFieldCircuits.map((circuit, circuitIndex) => {
                        const circuitTotalHp = circuit.units.reduce(
                          (sum, unit) => sum + (Number(unit.hp) || 0),
                          0
                        );
                        const circuitTotalKw = circuitTotalHp * 3.5;
                        const recommendedEev = circuitTotalHp > 0 ? Math.ceil(circuitTotalHp / 10) : 0;
                        const maxUnits = getAhuFieldMaxUnits();
                        const canAddUnit = circuit.units.length < maxUnits;

                        return (
                          <div
                            key={`field-circuit-${circuitIndex}`}
                            style={{
                              borderRadius: 16,
                              border: "1px solid #e2e8f0",
                              background: "#fff",
                              padding: 12,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                                alignItems: "center",
                                marginBottom: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 900, color: "#111827" }}>
                                  Devre {circuitIndex + 1}
                                </div>
                                <div style={{ color: "#64748b", fontSize: 12, marginTop: 3 }}>
                                  Toplam: {formatNumber(circuitTotalHp)} HP / {formatNumber(circuitTotalKw)} kW
                                  {circuitTotalHp > 0 ? ` • Önerilen EEV: ${recommendedEev}` : ""}
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => addAhuFieldUnit(circuitIndex)}
                                disabled={!canAddUnit}
                                style={{
                                  border: "none",
                                  borderRadius: 999,
                                  padding: "8px 12px",
                                  background: canAddUnit ? "#111827" : "#e5e7eb",
                                  color: canAddUnit ? "#fff" : "#94a3b8",
                                  fontWeight: 900,
                                  cursor: canAddUnit ? "pointer" : "not-allowed",
                                }}
                              >
                                + Dış Ünite Ekle
                              </button>
                            </div>

                            {circuit.units.length > 0 ? (
                              <div style={{ display: "grid", gap: 8 }}>
                                {circuit.units.map((unit, unitIndex) => (
                                  <div
                                    key={`field-unit-${circuitIndex}-${unitIndex}`}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr auto",
                                      gap: 8,
                                      alignItems: "center",
                                    }}
                                  >
                                    <select
                                      style={{ ...inputStyle, marginBottom: 0 }}
                                      value={`${unit.hp}|${unit.model}`}
                                      onChange={(e) =>
                                        updateAhuFieldUnit(circuitIndex, unitIndex, e.target.value)
                                      }
                                    >
                                      {ahuFieldOutdoorOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>

                                    <button
                                      type="button"
                                      onClick={() => removeAhuFieldUnit(circuitIndex, unitIndex)}
                                      style={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: 12,
                                        border: "1px solid #fecaca",
                                        background: "#fff1f2",
                                        color: "#991b1b",
                                        fontWeight: 900,
                                        cursor: "pointer",
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div
                                style={{
                                  borderRadius: 12,
                                  background: "#f8fafc",
                                  border: "1px dashed #cbd5e1",
                                  color: "#64748b",
                                  padding: "10px 12px",
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              >
                                Bu devre için sahada uygulanan dış ünite seçilmedi.
                              </div>
                            )}

                            <div style={{ marginTop: 10 }}>
                              <label style={{ ...labelStyle, fontSize: 13 }}>
                                Sahada Kullanılan EEV Adedi
                              </label>
                              <input
                                style={{ ...inputStyle, marginBottom: 0 }}
                                type="number"
                                min="0"
                                value={circuit.eev}
                                onChange={(e) => updateAhuFieldCircuitEev(circuitIndex, e.target.value)}
                                placeholder={circuitTotalHp > 0 ? `Öneri: ${recommendedEev}` : "Bu devrede EEV varsa girin"}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <label style={labelStyle}>Saha Notu / Açıklama</label>
                      <textarea
                        style={{
                          ...inputStyle,
                          minHeight: 80,
                          resize: "vertical",
                          marginBottom: 0,
                        }}
                        value={ahuFieldNote}
                        onChange={(e) => setAhuFieldNote(e.target.value)}
                        placeholder="Sahada uygulanan özel durum, geçmiş uygulama notu vb."
                      />
                    </div>
                  </div>
                )}

              </div>

              <div style={{ display: "grid", gap: 18 }}>
                <div style={cardStyle}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 14,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 18,
                        color: "#111827",
                      }}
                    >
                      Hesap Özeti
                    </div>

                    <button
                      onClick={generateAhuPDF}
                      title="AHU PDF çıktısı"
                      style={{
                        padding: "8px 12px",
                        borderRadius: 12,
                        border: "1px solid #cbd5e1",
                        background: "#f8fafc",
                        color: "#111827",
                        fontWeight: 900,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      PDF
                    </button>
                  </div>

                  <div
                    style={{
                      borderRadius: 16,
                      padding: "16px 18px",
                      marginBottom: 14,
                      background: ahuCalc.allOk ? "#16a34a" : "#dc2626",
                      color: "#fff",
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.95 }}>
                      GENEL DURUM
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>
                      {ahuCalc.allOk ? "UYGUN" : "KONTROL GEREKİYOR"}
                    </div>
                  </div>

                  {!ahuCalc.allOk && (
                    <div
                      style={{
                        borderRadius: 14,
                        background: "#fee2e2",
                        border: "1px solid #fecaca",
                        color: "#991b1b",
                        padding: "12px 14px",
                        fontWeight: 700,
                        marginBottom: 14,
                      }}
                    >
                      {ahuCalc.systemReason}
                    </div>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Gerekli Toplam HP</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                        {ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.requiredTotalHp)} HP` : "-"}
                      </div>
                    </div>

                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Gerekli Toplam kW</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                        {ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.resolvedCapacityKw)} kW` : "-"}
                      </div>
                    </div>

                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Önerilen Devre Sayısı</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                        {ahuCalc.autoPlan?.circuitCount || "-"}
                      </div>
                    </div>

                    <div style={summaryBoxStyle}>
                      <div style={{ color: "#64748b", fontSize: 12 }}>Kullanılan Devre Sayısı</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                        {ahuCalc.activePlan ? ahuCalc.circuitCount : "-"}
                      </div>
                    </div>
                  </div>

                  <div style={{ ...summaryBoxStyle, marginBottom: 10 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Teorik Debi Karşılığı</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                      {ahuCalc.hasLoadInput ? `${formatNumber(ahuCalc.theoreticalAirflowM3h)} m³/h` : "-"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                      Ham HP ihtiyacı × 480. Bu değer seçilen dış ünitenin debi aralığı değildir.
                    </div>
                  </div>

                  <div style={{ ...summaryBoxStyle, marginBottom: 10 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Girilen Santral Debisi</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                      {ahuCalc.hasAirflow ? `${formatNumber(ahuCalc.resolvedAirflowM3h)} m³/h` : "Girilmedi"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                      {ahuCalc.hasAirflow
                        ? `Girilen debi seçilen dış ünite aralığına göre kontrol edilir.`
                        : `Debi zorunlu alandır. Girilmediği için genel durum kontrol gerekiyor olarak kalır.`}
                    </div>
                  </div>

                  <div style={{ ...summaryBoxStyle, marginBottom: 10 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Toplam Seçilen Dış Ünite</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                      {ahuCalc.activePlan ? `${formatNumber(ahuCalc.totalSelectedHp)} HP / ${formatNumber(ahuCalc.totalSelectedKw)} kW` : "Seçim bekleniyor"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>
                      Toplam EEV: {ahuCalc.activePlan ? ahuCalc.totalEev : "-"}
                    </div>
                  </div>

                  <div style={{ ...summaryBoxStyle, marginBottom: 10 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Seçilen Dış Ünite Debi Aralığı</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                      {ahuCalc.activePlan ? `${formatNumber(ahuCalc.minAirflow)} – ${formatNumber(ahuCalc.maxAirflow)} m³/h` : "Seçim bekleniyor"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                      {ahuCalc.activePlan ? `Standart debi: ${formatNumber(ahuCalc.selectedStandardAirflowM3h)} m³/h` : "Kapasite veya debi girildiğinde hesaplanır."}
                    </div>
                  </div>

                  <div style={{ ...summaryBoxStyle, marginBottom: 14 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Ürün Bazlı Yuvarlama</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                      {ahuCalc.activePlan ? `+${formatNumber(ahuCalc.roundedUpHp)} HP / +${formatNumber(ahuCalc.roundedUpKw)} kW` : "-"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>
                      Ham ihtiyaç mevcut dış ünite ürün kademesine yukarı tamamlanır
                    </div>
                  </div>

                  <div style={{ ...summaryBoxStyle, marginBottom: 14 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Dış Ünite Seçim Kriteri</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginTop: 4 }}>
                      {ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.label || "-" : "-"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                      {ahuCalc.hasLoadInput ? ahuCalc.selectionBasis?.reason || "-" : "Kapasite veya debi girildiğinde seçim kriteri hesaplanır."}
                    </div>
                  </div>

                  {false && ahuFieldCompareEnabled && (
                    <div
                      style={{
                        marginTop: 14,
                        borderRadius: 16,
                        padding: "14px 16px",
                        background: ahuFieldComparison.allOk ? "#dcfce7" : "#fee2e2",
                        border: `1px solid ${ahuFieldComparison.allOk ? "#86efac" : "#fecaca"}`,
                        color: ahuFieldComparison.allOk ? "#166534" : "#991b1b",
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.95 }}>
                        SAHA UYGULAMASI KARŞILAŞTIRMASI
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 900, marginTop: 4 }}>
                        {ahuFieldComparison.allOk ? "UYGUN" : "UYGUN DEĞİL / KONTROL GEREKİYOR"}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.5, marginTop: 8 }}>
                        {ahuFieldComparison.allOk
                          ? "Olması gereken hesap ile sahada uygulanan seçim uyumlu."
                          : ahuFieldComparison.summary}
                      </div>
                    </div>
                  )}
                </div>

                <div style={cardStyle}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                      color: "#111827",
                      marginBottom: 14,
                    }}
                  >
                    Seçilen Cihaz Modelleri
                  </div>

                  {ahuCalc.circuitModelGroups && ahuCalc.circuitModelGroups.length > 0 ? (
                    <div style={{ display: "grid", gap: 14 }}>
                      {ahuCalc.circuitModelGroups.map((circuit) => (
                        <div key={circuit.circuitNo} style={summaryBoxStyle}>
                          <div
                            style={{
                              fontWeight: 800,
                              fontSize: 16,
                              color: "#111827",
                              marginBottom: 8,
                            }}
                          >
                            Devre {circuit.circuitNo} — {formatNumber(circuit.totalHp)} HP /{" "}
                            {formatNumber(circuit.totalKw)} kW
                          </div>

                          <div style={{ display: "grid", gap: 6 }}>
                            {circuit.units.map((unit) => (
                              <div
                                key={`${circuit.circuitNo}-${unit.unitNo}-${unit.hp}`}
                                style={{ color: "#0f172a", fontSize: 14 }}
                              >
                                <strong>
                                  {unit.unitNo}. Ünite — {unit.hp} HP
                                </strong>
                                <br />
                                {unit.models.join(" / ") || "-"}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={summaryBoxStyle}>Uygun cihaz seçimi bulunamadı.</div>
                  )}
                </div>
              </div>
            </div>


            {false && ahuFieldCompareEnabled && (
              <div style={cardStyle}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 18,
                    color: "#111827",
                    marginBottom: 14,
                  }}
                >
                  Saha Uygulaması Karşılaştırması
                </div>

                <div
                  style={{
                    borderRadius: 16,
                    padding: "14px 16px",
                    marginBottom: 14,
                    background: ahuFieldComparison.allOk ? "#dcfce7" : "#fee2e2",
                    border: `1px solid ${ahuFieldComparison.allOk ? "#86efac" : "#fecaca"}`,
                    color: ahuFieldComparison.allOk ? "#166534" : "#991b1b",
                    fontWeight: 800,
                    lineHeight: 1.5,
                  }}
                >
                  {ahuFieldComparison.allOk
                    ? "UYGUN: Sahada uygulanan seçim, olması gereken hesapla uyumlu."
                    : `UYGUN DEĞİL: ${ahuFieldComparison.summary}`}
                </div>

                {ahuFieldComparison.appliedCircuits.length > 0 && (
                  <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
                    {ahuFieldComparison.appliedCircuits.map((circuit) => (
                      <div key={`applied-circuit-${circuit.circuitNo}`} style={summaryBoxStyle}>
                        <div style={{ fontWeight: 900, color: "#111827", marginBottom: 8 }}>
                          Sahada Uygulanan Devre {circuit.circuitNo} — {formatNumber(circuit.totalHp)} HP / {formatNumber(circuit.totalKw)} kW
                        </div>
                        <div style={{ color: "#334155", fontSize: 13, lineHeight: 1.6 }}>
                          {circuit.units.map((unit, unitIndex) => (
                            <div key={`applied-unit-${circuit.circuitNo}-${unitIndex}`}>
                              {unitIndex + 1}. Ünite — {unit.model} / {unit.hp} HP
                            </div>
                          ))}
                          <div style={{ marginTop: 6, fontWeight: 800 }}>
                            Sahada Kullanılan EEV: {circuit.hasEev ? circuit.eevNumber : "Girilmedi"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: "grid", gap: 10 }}>
                  {ahuFieldComparison.rows.map((row) => (
                    <div
                      key={row.label}
                      style={{
                        borderRadius: 14,
                        border: `1px solid ${row.ok ? "#86efac" : "#fecaca"}`,
                        background: row.ok ? "#f0fdf4" : "#fff1f2",
                        padding: "12px 14px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 90px",
                          gap: 10,
                          alignItems: "start",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                            Olması Gereken
                          </div>
                          <div style={{ fontWeight: 800, color: "#111827", marginTop: 4 }}>
                            {row.expected}
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
                            Sahada Uygulanan
                          </div>
                          <div style={{ fontWeight: 800, color: "#111827", marginTop: 4 }}>
                            {row.applied}
                          </div>
                        </div>

                        <div
                          style={{
                            justifySelf: "end",
                            borderRadius: 999,
                            padding: "5px 9px",
                            fontSize: 11,
                            fontWeight: 900,
                            background: row.ok ? "#dcfce7" : "#fee2e2",
                            color: row.ok ? "#166534" : "#991b1b",
                            border: `1px solid ${row.ok ? "#86efac" : "#fecaca"}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.ok ? "UYGUN" : "UYGUN DEĞİL"}
                        </div>
                      </div>

                      <div style={{ marginTop: 8, color: row.ok ? "#166534" : "#991b1b", fontWeight: 700, fontSize: 13 }}>
                        {row.label}: {row.reason}
                      </div>
                    </div>
                  ))}
                </div>

                {ahuFieldNote.trim() && (
                  <div style={{ ...summaryBoxStyle, marginTop: 14 }}>
                    <div style={{ color: "#64748b", fontSize: 12 }}>Saha Notu</div>
                    <div style={{ fontWeight: 700, color: "#111827", marginTop: 4, lineHeight: 1.5 }}>
                      {ahuFieldNote}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={cardStyle}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 18,
                  color: "#111827",
                  marginBottom: 14,
                }}
              >
                Kontrol Sonuçları
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                }}
              >
                {ahuCalc.validationRows.map((row) => renderAhuValidationCard(row))}
              </div>
            </div>

            <div style={cardStyle}>
              <div
                style={{
                  fontWeight: 800,
                  fontSize: 18,
                  color: "#111827",
                  marginBottom: 14,
                }}
              >
                Minimum Dış Ünite Sayısı ile Uygun Alternatif Kombinasyonlar
              </div>

              {ahuCalc.alternatives && ahuCalc.alternatives.length > 0 ? (
                <div style={{ display: "grid", gap: 14 }}>
                  {ahuCalc.alternatives.map((alt) => (
                    <div
                      key={alt.id}
                      style={{
                        ...summaryBoxStyle,
                        background: alt.airflowOk ? "#f0fdf4" : "#fff1f2",
                        border: `1px solid ${alt.airflowOk ? "#bbf7d0" : "#fecdd3"}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          marginBottom: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ fontWeight: 800 }}>
                          {alt.rank}. Alternatif {alt.isRecommended ? "— Önerilen" : ""}
                        </div>
                        <div
                          style={{
                            padding: "4px 10px",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            background: alt.airflowOk ? "#dcfce7" : "#fee2e2",
                            color: alt.airflowOk ? "#166534" : "#991b1b",
                            border: `1px solid ${alt.airflowOk ? "#86efac" : "#fecaca"}`,
                          }}
                        >
                          {alt.badge}
                        </div>
                      </div>

                      <div style={{ color: "#334155", lineHeight: 1.7, marginBottom: 10 }}>
                        Devre Sayısı: {alt.circuitCount}
                        <br />
                        Toplam Seçilen Dış Ünite: {formatNumber(alt.totalHp)} HP /{" "}
                        {formatNumber(alt.totalKw)} kW
                        <br />
                        Toplam EEV: {alt.eevTotal}
                        <br />
                        Yukarı Yuvarlama: +{formatNumber(alt.roundedUpHp)} HP / +{formatNumber(alt.roundedUpKw)} kW
                        <br />
                        Dış Ünite Seçim Kriteri: {alt.selectionBasis?.label || "-"}
                        <br />
                        Girilen Debi: {alt.enteredAirflow ? `${formatNumber(alt.enteredAirflow)} m³/h` : "Girilmedi"}
                        <br />
                        Debi Aralığı: Min {formatNumber(alt.minAirflow)} m³/h / Standart {formatNumber(alt.totalHp * 480)} m³/h / Max {formatNumber(alt.maxAirflow)} m³/h
                      </div>

                      {alt.modelGroups && alt.modelGroups.length > 0 && (
                        <div style={{ marginBottom: 10, paddingTop: 10, borderTop: "1px solid #e5e7eb" }}>
                          <div style={{ fontWeight: 700, color: "#111827", marginBottom: 8, fontSize: 14 }}>
                            Seçilen Cihaz Modelleri
                          </div>
                          <div style={{ display: "grid", gap: 8 }}>
                            {alt.modelGroups.map((circuit) => (
                              <div key={circuit.circuitNo} style={{ color: "#334155", fontSize: 13, lineHeight: 1.6 }}>
                                <div style={{ fontWeight: 700 }}>
                                  Devre {circuit.circuitNo} — {formatNumber(circuit.totalHp)} HP / {formatNumber(circuit.totalKw)} kW
                                </div>
                                <div style={{ marginLeft: 12, marginTop: 4 }}>
                                  {circuit.units.map((unit) => (
                                    <div key={`${circuit.circuitNo}-${unit.unitNo}`}>
                                      {unit.unitNo}. Ünite — {unit.hp} HP
                                      <br />
                                      {unit.models.join(" / ") || "-"}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div
                        style={{
                          borderRadius: 12,
                          background: alt.airflowOk ? "#f0fdf4" : "#fff1f2",
                          border: `1px solid ${alt.airflowOk ? "#bbf7d0" : "#fecdd3"}`,
                          padding: "12px 14px",
                          color: alt.airflowOk ? "#166534" : "#991b1b",
                          fontWeight: 700,
                          marginBottom: 12,
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        {alt.airflowReason}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={summaryBoxStyle}>Uygun alternatif kombinasyon bulunamadı.</div>
              )}
            </div>


          </>
        )}
      </div>
    </div>
  );
}

export default App;